import { billingPt } from "./billing.pt";
import { hrPt } from "./hr.pt";
import { dashboardPt } from "./dashboard.pt";
import { paymentsPt } from "./payments.pt";
import { reportsPt } from "./reports.pt";
import type { MessageId } from "./fr";

/** European Portuguese catalog. Same keys and {placeholders} as fr.ts (checked by catalogs.t.ts). */
// Terms: feature = funcionalidade, feature enablement = ativação de funcionalidade, role = função,
// case (dossier) = processo, pro = profissional, client = cliente, job request (demande) = pedido,
// lead (opportunité) = oportunidade, report (signalement) = denúncia, reason (motif) = motivo,
// reveal (afficher) = mostrar, staff member = membro da equipa, settings = definições,
// audit log = registo de auditoria, sign out = terminar sessão, owner = proprietário.
export const pt: Record<MessageId, string> = {
  ...reportsPt,
  ...paymentsPt,
  ...dashboardPt,
  ...billingPt,
  ...hrPt,
  // ── Brand and shell ──
  "brand.name": "Walliyo",
  "brand.initial": "W",
  "brand.console": "Consola",
  "nav.aria": "Consola",
  "nav.new": "NOVO",
  "nav.group.configuration": "Configuração",
  "overview.eachDepartment": "Cada departamento, uma linha",
  "overview.dept.marketplace": "Operações de mercado",
  "overview.dept.growth": "Vendas e crescimento",
  "overview.dept.support": "Apoio ao cliente",
  "overview.dept.trust": "Confiança e segurança",
  "overview.dept.finance": "Finanças",
  "overview.dept.people": "Recursos humanos",
  "overview.dept.administration": "Administração",
  "overview.dept.product": "Engenharia",
  "overview.clickThrough": "clique para ir lá",
  "overview.numberToWatch": "O número a vigiar",
  "overview.whereWalliyoWorks": "Onde a Walliyo trabalha",
  "overview.noCitiesYet": "Sem pedidos este mês.",
  "overview.points": "{points} pontos",
  "overview.title.people.walliyoMonth": "Walliyo, {month}",
  "overview.people.tab.waiting": "O que o espera",
  "overview.people.tab.company": "A empresa",
  "overview.people.tab.weeks": "As próximas semanas",
  "overview.people.askForTimeOff": "Pedir férias",
  "overview.people.joinedThisMonth": "{count} entraram este mês",
  "overview.people.joinedThisMonth.one": "1 entrou este mês",
  "overview.people.candidates": "{count} candidatos",
  "overview.people.candidates.one": "1 candidato",
  "overview.people.medianStay": "Anos, permanência mediana",
  "overview.people.whereEverybodyIs": "Onde está cada um",
  "overview.people.oneLinePerTeam": "uma linha por equipe",
  "overview.people.noTeamsYet": "Ainda sem equipes.",
  "overview.people.noLead": "sem responsável",
  "overview.people.nobodyCovering": "Ninguém a cobrir",
  "overview.people.thinWeek": "{team} perde {away} de {size} na semana de {week}.",
  "overview.people.whoIsWhere": "Quem está onde",
  "overview.people.fourWeeks": "quatro semanas, e o que cai em cada",
  "overview.people.allTimeOff": "Todas as férias",
  "overview.people.quietWeeks": "Nada cai nas próximas quatro semanas.",
  "overview.people.mark.payrollCloses": "folha fecha {on}",
  "overview.people.mark.reviewsClose": "avaliações fecham {on}",
  "overview.people.span.leave": "{days} dias de férias",
  "overview.people.span.leave.one": "1 dia de férias",
  "overview.people.span.leavePending": "{days} dias · a decidir",
  "overview.people.span.leavePending.one": "1 dia · a decidir",
  "overview.people.span.starts": "começa",
  "overview.people.task.candidates": "{count} candidatos à sua espera",
  "overview.people.detail.candidates": "para {second} vagas abertas",
  "marketplace.seeAll": "Ver todos os {count}",
  "marketplace.findAPro": "Encontrar um profissional",
  "marketplace.theMarket": "O mercado, esta semana",
  "overview.title.marketplace.theMarket": "O mercado, esta semana",
  "overview.lede.marketplace.nobodyAnswered": "{first} pessoas pediram algo e ninguém respondeu.",
  "overview.lede.marketplace.demandNoSupply":
    "{first} ofícios são procurados onde nenhum profissional tem a competência.",
  "overview.lede.marketplace.quiet": "Nada foi publicado esta semana.",
  "overview.lede.marketplace.allAnswered": "Todos os trabalhos desta semana receberam orçamento.",
  "overview.people.link.employees": "Funcionários",
  "overview.people.link.recruiting": "Recrutamento",
  "overview.people.link.learning": "Formação",
  "overview.people.link.teams": "Equipes",
  "overview.administration.askedNotAnswered": "Pedido, sem resposta",
  "overview.administration.oldestFirst": "o mais antigo primeiro",
  "overview.administration.nobodyWaiting": "Ninguém aguarda resposta.",
  "overview.administration.row.wantsUntil": "{subject} quer poder {asked}",
  "overview.administration.row.wantsForever": "{subject} quer poder {asked}, sem data de fim",
  "overview.administration.until": "até {on}",
  "overview.administration.noEndAsked": "nenhuma data de fim pedida",
  "overview.administration.hoursWaiting": "{hours} horas à espera",
  "overview.administration.hoursWaiting.one": "1 hora à espera",
  "overview.administration.pastTheDeadline": "prazo ultrapassado",
  "overview.administration.nobodyTookItBack": "Ninguém o retomou",
  "overview.administration.noEndDate": "{count} acessos não têm data de fim.",
  "overview.administration.noEndDate.one": "1 acesso não tem data de fim.",
  "overview.administration.belongToLeavers": "{leavers} pertencem a alguém que saiu.",
  "overview.administration.belongToLeavers.one": "1 pertence a alguém que saiu.",
  "overview.administration.reviewThem": "Revê-los",
  "overview.administration.criticalCapabilities": "Capacidades críticas",
  "overview.administration.ifAnyReachesOne":
    "Se alguma delas chegar a uma pessoa, ninguém consegue cobrir a sua ausência.",
  "overview.figure.holdCritical": "Detêm algo crítico",
  "overview.lede.administration.waitingAndGrants":
    "{first} pessoas aguardam resposta, e {second} acessos não têm data de fim.",
  "overview.lede.administration.waiting": "{first} pessoas aguardam resposta.",
  "overview.lede.administration.grants": "{first} acessos não têm data de fim e ninguém os retomou.",
  "overview.lede.administration.nothingWaiting": "Ninguém aguarda resposta.",
  "overview.growth.step.signedUp": "Inscritos",
  "overview.growth.step.verified": "Verificados",
  "overview.growth.step.addedSkill": "Adicionaram um ofício",
  "overview.growth.step.quotedOnce": "Orçaram uma vez",
  "overview.growth.step.wonAJob": "Ganharam um trabalho",
  "overview.growth.lost.signedUp": "nunca se inscreveram",
  "overview.growth.lost.verified": "desistiram",
  "overview.growth.lost.addedSkill": "nunca o fizeram",
  "overview.growth.lost.quotedOnce": "nunca orçaram",
  "overview.growth.lost.wonAJob": "nunca ganharam",
  "overview.growth.stuck": "Parados, e vale a chamada",
  "overview.growth.stuckWho": "verificados, num ofício procurado, nunca orçaram",
  "overview.growth.nobodyStuck": "Ninguém está parado.",
  "overview.growth.row.workGoingUnanswered": "{subject}",
  "overview.growth.why.workGoingUnanswered": "verificado há {days} dias · há trabalho no seu ofício sem resposta",
  "overview.growth.row.neverQuoted": "{subject}",
  "overview.growth.why.neverQuoted": "verificado há {days} dias · nunca orçou nada",
  "overview.growth.promoCost": "Deu {given} em descontos contra {earned} em taxas dos profissionais que a usaram.",
  "overview.growth.promoUsed": "{used} profissionais a usaram",
  "overview.growth.beforeYouCampaign": "Antes de fazer campanha",
  "overview.growth.notificationsAreFree":
    "As notificações na aplicação chegam a estes profissionais sem custo. O SMS custa por mensagem, e aqui os dados pagam-se à hora.",
  "overview.action.call": "Ligar",
  "overview.lede.growth.neverQuoted":
    "{first} profissionais inscreveram-se e nunca orçaram nada. É esse o trabalho todo.",
  "overview.lede.growth.nobodySignedUp": "Ninguém se inscreveu este mês.",
  "overview.lede.growth.allWorking": "Todos os profissionais inscritos orçaram algo.",
  "overview.support.dueInMinutes.one": "prazo em 1 minuto",
  "overview.support.dueInHours.one": "prazo em 1 hora",
  "overview.support.overdue.one": "1 minuto além do prazo",
  "overview.support.nobodyOwnsWhy.one":
    "Um caso sem responsável é um caso que ninguém responde. O mais antigo está há 1 dia.",
  "overview.people.days.one": "1 dia",
  "overview.people.payrollOpen.one": "{month} está aberto. Fecha amanhã.",
  "overview.people.detail.leave.one": "Pedido há 1 dia · você é o responsável",
  "overview.finance.waiting.one": "1 dia à espera",
  "overview.trust.row.appealWaiting.one": "Recurso à espera há 1 dia",
  "overview.trust.legalHoldsWhy.one":
    "1 conta está retida. Nada sobre ela pode ser apagado, nem por um pedido de dados.",
  "overview.product.unconfirmed.one": "1 retorno não confirmado, há {minutes} minutos.",
  "overview.product.whatIsBroken": "O que está quebrado",
  "overview.product.worstFirst": "o pior primeiro",
  "overview.product.nothingBroken": "Nada está quebrado.",
  "overview.product.unconfirmed": "{count} retornos não confirmados, o mais antigo há {minutes} minutos.",
  "overview.product.unconfirmedWhy": "Cada um é um profissional que pagou e não está marcado como pago.",
  "overview.product.replayThem": "Reexecutá-los",
  "overview.product.rolledOut": "Lançado aos clientes",
  "overview.product.tally.everybody": "Todos",
  "overview.product.tally.someAccounts": "Algumas contas",
  "overview.product.tally.off": "Desligado",
  "overview.figure.rolloutsInFlight": "Lançamentos em curso",
  "overview.product.row.incidentOpen": "{subject}",
  "overview.product.why.incidentOpen": "aberto há {amount} · os clientes veem o aviso",
  "overview.product.row.ruleScheduled": "A regra entra em vigor {at}",
  "overview.product.why.ruleScheduled": "{subject} {amount} · ninguém aprovou",
  "overview.product.row.experimentRunning": "{subject} em {days}% dos clientes",
  "overview.product.why.experimentRunning": "em curso desde {on} · pronto para decidir",
  "overview.figure.incidentsOpen": "Incidentes abertos",
  "overview.lede.product.incidentAndCallbacks":
    "{first} incidente está aberto, e {second} retornos de pagamento falharam.",
  "overview.lede.product.incident": "{first} incidente está aberto.",
  "overview.lede.product.callbacks": "{first} retornos de pagamento falharam e ninguém os reexecutou.",
  "overview.lede.product.rulesScheduled": "{first} regras entram em vigor sem ninguém presente.",
  "overview.lede.product.nothingBroken": "Nada está quebrado.",
  "overview.finance.aboveLimit": "Acima do limite de alguém",
  "overview.finance.theyAskedYouDecide": "eles pediram, você decide",
  "overview.finance.nothingWaitingHere": "Nada aguarda aprovação.",
  "overview.finance.theirLimit": "limite {ceiling}",
  "overview.finance.noLimit": "não tem limite",
  "overview.finance.waiting": "{days} dias à espera",
  "overview.finance.today": "hoje",
  "overview.finance.subscriptions": "Assinaturas",
  "overview.finance.tally.endingSoon": "Renovação em 7 dias",
  "overview.finance.tally.suspended": "Pagamento falhou",
  "overview.finance.tally.ended": "Terminadas, não renovadas",
  "overview.finance.promosRunning": "Promoções em curso",
  "overview.finance.promoEnds": "{name} termina em {on}.",
  "overview.finance.promoNoEnd": "{name} não tem data de fim.",
  "overview.finance.promoCost": "Deu {given} em descontos contra {earned} em taxas.",
  "overview.figure.feesCollected": "Taxas recebidas",
  "overview.figure.jobsBilled": "Trabalhos faturados",
  "overview.figure.renewalsFailed": "Renovações falhadas",
  "overview.lede.finance.waitingOnApproval": "{first} correções aguardam alguém que possa aprová-las.",
  "overview.lede.finance.renewalsFailed": "{first} renovações falharam e ninguém as retomou.",
  "overview.lede.finance.renewingSoon": "{first} assinaturas renovam esta semana.",
  "overview.lede.finance.nothingWaiting": "Nada aguarda aprovação.",
  "overview.trust.needsDecision": "A decidir hoje",
  "overview.trust.mostSeriousFirst": "o mais grave primeiro",
  "overview.trust.nothingUrgentHere": "Nada a decidir hoje.",
  "overview.trust.verificationQueue": "Fila de verificação",
  "overview.trust.whyVerify": "Um profissional à espera de verificação não pode ser encontrado nem contratado.",
  "overview.trust.tally.underADay": "À espera há menos de 24 h",
  "overview.trust.tally.overADay": "Mais de 24 h",
  "overview.trust.legalHolds": "Retenções legais",
  "overview.trust.legalHoldsWhy":
    "{count} contas estão retidas. Nada sobre elas pode ser apagado, nem por um pedido de dados.",
  "overview.trust.row.criticalUnreferred": "{subject}",
  "overview.trust.why.criticalUnreferred": "crítico · não encaminhado à polícia",
  "overview.trust.row.restrictionExpiring": "A restrição termina sozinha em {on}",
  "overview.trust.why.restrictionExpiring": "{subject} · levante-a, ou ela se levanta sozinha",
  "overview.trust.row.appealWaiting": "Recurso à espera há {days} dias",
  "overview.trust.why.appealWaiting": "uma conta restringida pediu reapreciação",
  "overview.figure.underLegalHold": "Sob retenção legal",
  "overview.lede.trust.criticalUnreferred": "{first} denúncias são críticas e nenhuma foi encaminhada.",
  "overview.lede.trust.waitingOverADay": "{first} profissionais esperam há mais de um dia pela verificação.",
  "overview.lede.trust.waitingToVerify": "{first} profissionais esperam pela verificação.",
  "overview.lede.trust.nothingUrgent": "Nada a decidir hoje.",
  "overview.support.runningOut": "O tempo está a acabar",
  "overview.support.soonestFirst": "prazo mais próximo primeiro",
  "overview.support.nothingDue": "Nenhum prazo está próximo.",
  "overview.support.whereFrom": "De onde vieram",
  "overview.support.noChannels": "Nenhum caso aberto.",
  "overview.support.channel.chat": "No aplicativo",
  "overview.support.channel.phone": "Telefone",
  "overview.support.channel.email": "E-mail",
  "overview.support.nobodyOwns": "{count} sem responsável",
  "overview.support.nobodyOwnsWhy":
    "Um caso sem responsável é um caso que ninguém responde. O mais antigo está há {days} dias.",
  "overview.support.takeOldest": "Assumir o mais antigo",
  "overview.support.row.mine": "seu",
  "overview.support.row.theirs": "de outra pessoa",
  "overview.support.row.unclaimed": "sem responsável",
  "overview.support.dueInMinutes": "prazo em {minutes} minutos",
  "overview.support.dueInHours": "prazo em {hours} horas",
  "overview.support.overdue": "{minutes} minutos além do prazo",
  "overview.figure.toFirstReply": "Até a primeira resposta",
  "overview.action.reply": "Responder",
  "overview.action.claim": "Assumir",
  "overview.lede.support.breaching": "{first} casos vão perder o prazo.",
  "overview.lede.support.breachingSomeYours": "{first} casos vão perder o prazo. {second} deles são seus.",
  "overview.lede.support.unclaimed": "{first} casos esperam que alguém os assuma.",
  "overview.lede.support.nothingBreaching": "Nenhum caso vai perder o prazo.",
  "overview.against": "contra {month}",
  "overview.title.executive.walliyoMonth": "Walliyo, {month}",
  "overview.title.people.thingsNeedYou": "{count} coisas precisam de você",
  "overview.title.people.nothingNeedsYou": "Nada precisa de você",
  "overview.title.support.bonjour": "Olá, {name}",
  "overview.title.trust.safetyToday": "Segurança, hoje",
  "overview.title.finance.theMoney": "O dinheiro, esta semana",
  "overview.title.product.themOrUs": "É deles ou é nosso",
  "overview.title.growth.gettingProsWorking": "Pôr os profissionais a trabalhar",
  "overview.title.administration.whoMayDoWhat": "Quem pode fazer o quê",
  "overview.title.marketplace.plain": "Visão geral",
  "overview.title.legal.plain": "Visão geral",
  "overview.lede.executive.grewFaster": "O mercado cresceu. O que ele não conseguiu atender cresceu mais rápido.",
  "overview.lede.executive.wentUnanswered": "{first} pedidos ficaram sem resposta este mês.",
  "overview.lede.executive.quiet": "Nada foi publicado este mês.",
  "overview.lede.executive.holding": "{second} pedidos publicados, e todos encontraram resposta.",
  "overview.people.allClear": "Nada precisa de você. Tudo foi tratado.",
  "overview.people.theCompany": "A empresa",
  "overview.people.employees": "funcionários",
  "overview.people.teams": "equipes",
  "overview.people.yours": "Seu",
  "overview.people.leaveLeft": "Férias restantes",
  "overview.people.days": "{days} dias",
  "overview.people.noAllowance": "não definido",
  "overview.people.coursesDue": "Formações a fazer",
  "overview.people.payroll": "Folha de pagamento",
  "overview.people.payrollOpen": "{month} está aberto. Fecha em {days} dias.",
  "overview.people.payrollDue": "{month} está aberto e a data de fecho já passou.",
  "overview.people.payrollClosed": "{month} está fechado.",
  "overview.people.linesChecked": "{checked} de {total} linhas verificadas",
  "overview.people.task.leave": "{subject} pede {count} dias de férias",
  "overview.people.detail.leave": "Pedido há {days} dias · você é o responsável",
  "overview.people.task.grantsNoEndDate": "{count} acessos não têm data de fim",
  "overview.people.detail.grantsNoEndDate": "{second} pertencem a pessoas que você lidera",
  "overview.people.task.reviews": "{count} avaliações são suas para escrever",
  "overview.people.detail.reviews": "fecha em {on}",
  "overview.people.task.starting": "{subject} começa em {on}",
  "overview.people.detail.starting": "função já atribuída",
  "overview.people.task.startingNoRole": "{subject} começa em {on}",
  "overview.people.detail.startingNoRole": "nenhuma função atribuída",
  "overview.action.decide": "Decidir",
  "overview.action.review": "Revisar",
  "overview.action.write": "Escrever",
  "overview.action.setUp": "Configurar",
  "overview.action.open": "Abrir",
  "overview.hero.jobsPosted": "Pedidos publicados",
  "overview.hero.gotAQuote": "Com orçamento",
  "overview.hero.fees": "Comissões, GNF",
  "overview.hero.activePros": "Profissionais ativos",
  "overview.hero.employees": "Colaboradores",
  "overview.headline.unanswered": "{count} pedidos sem resposta · {second} ofícios sem cobertura",
  "overview.headline.neverQuoted": "{count} profissionais inscritos e nunca responderam",
  "overview.headline.breaching": "{count} processos quase fora de prazo · {second} sem responsável",
  "overview.headline.criticalUnreferred":
    "{count} denúncias críticas não encaminhadas · {second} verificações pendentes",
  "overview.headline.approvalsWaiting": "{count} correções à espera · {second} em atraso",
  "overview.headline.approvalsWaiting.zero": "{count} correções à espera · nada em atraso",
  "overview.headline.grantsWithoutExpiry": "{count} acessos sem data de fim · {second} pedidos de acesso",
  "overview.headline.incidentsOpen": "{count} incidente aberto · {second} retornos falhados",
  "overview.headline.startingAndPayroll": "{count} a começar · folha de pagamento aberta",
  "overview.watch.demandOutrunningSupply":
    "Os pedidos sobem {first}% e a parcela que recebe orçamento cai {second} pontos. A procura chega mais depressa do que a oferta consegue responder.",
  "overview.watch.nobodyAnswered": "{first} pedidos foram publicados sem que ninguém orçasse.",
  "roles.new.department": "Departamento",
  "roles.new.pickDepartment": "Escolher um departamento…",
  "roles.new.departmentNeeded":
    "Escolha o departamento do perfil: um testador QA é Produto e engenharia, um contabilista é Finanças.",
  "screen.experiments": "Experiências",
  "screen.leads": "Contactos",
  "screen.campaigns": "Campanhas",
  "screen.referrals": "Indicações",
  "experiments.title": "Experiências",
  "experiments.sub": "Ligar uma funcionalidade para uma parte dos clientes e comparar.",
  "experiments.loadFailed": "Não foi possível carregar as experiências.",
  "experiments.none": "Nenhuma experiência.",
  "experiments.start": "Iniciar uma experiência",
  "experiments.created": "{reference} criada",
  "experiments.moved": "{reference} a {percent}%",
  "experiments.ship": "Generalizar",
  "experiments.stop": "Parar",
  "experiments.sticky": "Uma pessoa fica no seu grupo: o grupo é calculado, nunca sorteado.",
  "experiments.col.name": "Experiência",
  "experiments.col.feature": "Funcionalidade",
  "experiments.col.share": "Parte",
  "experiments.col.status": "Estado",
  "experiments.col.ramp": "Alargar",
  "experiments.status.draft": "Rascunho",
  "experiments.status.running": "A decorrer",
  "experiments.status.stopped": "Parada",
  "experiments.status.shipped": "Generalizada",
  "experiments.startTitle": "Iniciar uma experiência",
  "experiments.startBody": "Começa a 0%: criar e abrir são duas decisões.",
  "experiments.startSubmit": "Criar",
  "experiments.featureNeeded": "Escolha uma funcionalidade de cliente.",
  "experiments.pickFeature": "Escolher…",
  "experiments.field.name": "O que se tenta",
  "experiments.field.feature": "Funcionalidade de cliente",
  "experiments.field.featureHint": "Uma experiência corre sobre clientes, nunca sobre pessoal.",
  "experiments.field.metric": "O que se mede",
  "experiments.field.metricHint": "Lido por uma pessoa, não pelo código.",
  "leads.title": "Contactos",
  "leads.sub": "Profissionais que queremos e ainda não temos.",
  "leads.loadFailed": "Não foi possível carregar os contactos.",
  "leads.none": "Nenhum contacto.",
  "leads.add": "Adicionar um contacto",
  "leads.added": "{name} adicionado",
  "leads.moved": "{name} atualizado",
  "leads.markContacted": "Contactado",
  "leads.markLost": "Perdido",
  "leads.nameNeeded": "Dê o nome do profissional.",
  "leads.status.new": "Novo",
  "leads.status.contacted": "Contactado",
  "leads.status.signed_up": "Inscrito",
  "leads.status.lost": "Perdido",
  "leads.source.coverage_gap": "Falta de cobertura",
  "leads.source.referral": "Recomendação",
  "leads.source.inbound": "Entrada",
  "leads.source.other": "Outro",
  "leads.addTitle": "Adicionar um contacto",
  "leads.addBody": "Alguém que ainda não está na plataforma.",
  "leads.addSubmit": "Adicionar",
  "leads.field.name": "Nome",
  "leads.field.phone": "Telefone",
  "leads.field.trade": "Ofício",
  "leads.field.city": "Cidade",
  "leads.field.source": "Origem",
  "campaigns.title": "Campanhas",
  "campaigns.sub": "Mensagens a profissionais e clientes.",
  "campaigns.loadFailed": "Não foi possível carregar as campanhas.",
  "campaigns.none": "Nenhuma campanha.",
  "campaigns.draft": "Preparar uma campanha",
  "campaigns.drafted": "{reference} pronta: {count} destinatários",
  "campaigns.send": "Enviar",
  "campaigns.sent": "Enviada a {count} pessoas",
  "campaigns.wentTo": "Foi para {count}",
  "campaigns.budgetNeeded": "Uma campanha SMS precisa de orçamento.",
  "campaigns.onlyInactive": "Só quem não fez nada",
  "campaigns.inAppFree": "Grátis: a notificação chega na aplicação.",
  "campaigns.smsCosts": "Pago: cada SMS custa, e aqui os dados pagam-se à hora.",
  "campaigns.smsLength": "{count} caracteres (acima de 160 são dois SMS por pessoa)",
  "campaigns.channel.in_app": "Na aplicação",
  "campaigns.channel.sms": "SMS",
  "campaigns.audience.pros": "Profissionais",
  "campaigns.audience.customers": "Clientes",
  "campaigns.draftTitle": "Preparar uma campanha",
  "campaigns.draftBody": "O número de destinatários é recontado no envio.",
  "campaigns.draftSubmit": "Preparar",
  "campaigns.field.name": "Nome",
  "campaigns.field.channel": "Canal",
  "campaigns.field.audience": "Destinatários",
  "campaigns.field.body": "Mensagem",
  "campaigns.field.bodyHint": "Em francês, visível por todos.",
  "campaigns.field.budget": "Orçamento (GNF)",
  "campaigns.field.budgetHint": "O que se autoriza gastar.",
  "referrals.title": "Indicações",
  "referrals.sub": "Profissionais trazidos por outros profissionais.",
  "referrals.loadFailed": "Não foi possível carregar as indicações.",
  "referrals.none": "Nenhuma indicação.",
  "referrals.unused": "Código não usado",
  "referrals.paidOut": "{amount} {currency} pagos",
  "referrals.paidWhen": "Pago quando o profissional indicado ganha o primeiro trabalho.",
  "referrals.status.open": "Aberto",
  "referrals.status.signed_up": "Inscrito",
  "referrals.status.rewarded": "Pago",
  "referrals.status.expired": "Expirado",
  "screen.incidents": "Incidentes",
  "incidents.title": "Incidentes",
  "incidents.sub": "O que está avariado, e o que se diz às pessoas.",
  "incidents.loadFailed": "Não foi possível carregar os incidentes.",
  "incidents.openOne": "Abrir um incidente",
  "incidents.happeningNow": "A decorrer",
  "incidents.allWell": "Está tudo a funcionar.",
  "incidents.over": "Terminados",
  "incidents.open": "Abrir",
  "incidents.notAnnounced": "Não anunciado",
  "incidents.started": "Começou a {when}",
  "incidents.opened": "{reference} aberto",
  "incidents.updated": "{reference} atualizado",
  "incidents.status.open": "Aberto",
  "incidents.status.monitoring": "Em observação",
  "incidents.status.resolved": "Resolvido",
  "incidents.severity.degraded": "Degradado",
  "incidents.severity.down": "Fora de serviço",
  "incidents.openTitle": "Abrir um incidente",
  "incidents.openBody":
    "Enquanto estiver aberto, cada cliente vê um aviso. Deixe vazio para acompanhar o incidente sem anunciar nada.",
  "incidents.openSubmit": "Abrir",
  "incidents.titleNeeded": "Diga o que está avariado, em poucas palavras.",
  "incidents.causeNeeded": "Diga porque aconteceu antes de fechar.",
  "incidents.updateSubmit": "Guardar",
  "incidents.field.title": "O que está avariado",
  "incidents.field.severity": "Gravidade",
  "incidents.field.banner": "Mensagem aos clientes",
  "incidents.field.bannerHint": "Em francês, visível por todos. Vazio: ninguém é avisado.",
  "incidents.field.status": "Estado",
  "incidents.field.note": "Nota",
  "incidents.field.noteHint": "Vai para o histórico, que nada pode reescrever.",
  "incidents.field.cause": "Causa",
  "screen.overview": "Visão geral",
  "overview.sub": "O que hoje espera por si.",
  "overview.loadFailed": "Não foi possível carregar a visão geral.",
  "overview.needsYou": "O que precisa de si",
  "overview.nothingToShow": "Nada a mostrar: não detém nada do que este departamento consulta.",
  "overview.waitingDays": "{days} d à espera",
  "overview.figure.leaveToDecide": "Férias a decidir",
  "overview.figure.reviewsOwed": "Avaliações a escrever",
  "overview.figure.openRoles": "Vagas abertas",
  "overview.figure.startingSoon": "Entradas esta semana",
  "overview.figure.payrollOpen": "Processamentos abertos",
  "overview.figure.leaversStillHolding": "Saídos ainda com acesso",
  "overview.figure.accessRequests": "Pedidos de acesso",
  "overview.figure.grantsWithoutExpiry": "Acessos sem data de fim",
  "overview.figure.rolesHeldByNobody": "Perfis que ninguém detém",
  "overview.figure.staffWithSignIn": "Colaboradores ativos",
  "overview.figure.casesBreaching": "Processos quase fora de prazo",
  "overview.figure.casesMine": "Os seus processos",
  "overview.figure.casesUnclaimed": "Processos sem responsável",
  "overview.figure.casesOpen": "Processos abertos",
  "overview.figure.criticalUnreferred": "Denúncias críticas não encaminhadas",
  "overview.figure.verificationsWaiting": "Verificações pendentes",
  "overview.figure.verificationsOverADay": "À espera há mais de 24 h",
  "overview.figure.reportsOpen": "Denúncias abertas",
  "overview.figure.restrictionsToReview": "Restrições a rever",
  "overview.figure.legalHolds": "Retenções legais",
  "overview.figure.approvalsWaiting": "Correções a aprovar",
  "overview.figure.feesThisMonth": "Pagamentos este mês",
  "overview.figure.creditsOutstanding": "Créditos em aberto",
  "overview.figure.subscriptionsEndingSoon": "Subscrições a acabar em 7 d",
  "overview.figure.providerEventsFailed": "Retornos do fornecedor falhados",
  "overview.figure.rulesScheduled": "Regras agendadas",
  "overview.figure.featureExceptions": "Exceções de funcionalidade",
  "overview.figure.prosSignedUp": "Profissionais inscritos (30 d)",
  "overview.figure.prosApproved": "Profissionais aprovados (30 d)",
  "overview.figure.prosNeverQuoted": "Profissionais que nunca responderam",
  "overview.figure.prosWithWork": "Profissionais com trabalho",
  "overview.figure.promosRunning": "Promoções a decorrer",
  "overview.figure.jobsThisMonth": "Pedidos este mês",
  "overview.figure.customers": "Contas de clientes",
  "overview.item.caseDueOwned": "O seu processo está a esgotar o prazo",
  "overview.item.caseDueUnclaimed": "Processo sem responsável a esgotar o prazo",
  "screen.marketplaceOverview": "Visão geral",
  "screen.jobs": "Pedidos",
  "screen.providers": "Profissionais",
  "screen.categories": "Ofícios",
  "screen.marketplaceReviews": "Avaliações",
  "marketplace.title": "O mercado",
  "marketplace.sub": "O que foi pedido, quem o sabe fazer, e onde ninguém sabe.",
  "marketplace.loadFailed": "Não foi possível carregar o mercado.",
  "marketplace.posted": "Pedidos publicados",
  "marketplace.gotQuote": "Com orçamento",
  "marketplace.accepted": "Aceites",
  "marketplace.noBids": "Ninguém respondeu",
  "marketplace.toFirstQuote": "Até ao 1.º orçamento",
  "marketplace.hoursShort": "{hours} h",
  "marketplace.nobodyBid": "Ninguém respondeu a estes",
  "marketplace.nobodyBidHint": "Mais antigos primeiro.",
  "marketplace.allAnswered": "Tudo o que foi publicado teve resposta.",
  "marketplace.waitingDays": "{days} d à espera",
  "marketplace.noProHere": "aqui ninguém faz isto",
  "marketplace.prosNearby": "{count} profissionais por perto",
  "marketplace.demandNoSupply": "Procura sem oferta",
  "marketplace.demandNoSupplyHint": "Pedido nos últimos 30 dias, sem profissional no local.",
  "marketplace.everywhereCovered": "Cada cidade tem profissionais para o que pede.",
  "marketplace.activePros": "{count} profissionais podem trabalhar hoje",
  "marketplace.jobs": "Pedidos",
  "marketplace.jobsSub": "Cada pedido, e até onde chegou.",
  "marketplace.noJobs": "Nenhum pedido corresponde.",
  "marketplace.filter.all": "Todos",
  "marketplace.filter.no_quotes": "Sem orçamento",
  "marketplace.filter.requested": "Publicados",
  "marketplace.filter.quoted": "Com orçamento",
  "marketplace.filter.accepted": "Aceites",
  "marketplace.filter.cancelled": "Cancelados",
  "marketplace.status.requested": "Publicado",
  "marketplace.status.quoted": "Com orçamento",
  "marketplace.status.accepted": "Aceite",
  "marketplace.status.cancelled": "Cancelado",
  "marketplace.status.declined": "Recusado",
  "marketplace.status.expired": "Expirado",
  "marketplace.col.job": "Pedido",
  "marketplace.col.city": "Cidade",
  "marketplace.col.posted": "Publicado",
  "marketplace.col.quotes": "Orçamentos",
  "marketplace.col.estimate": "Estimativa",
  "marketplace.col.status": "Estado",
  "marketplace.providers": "Profissionais",
  "marketplace.providersSub": "Quem pode trabalhar, e quem ainda não trabalha.",
  "marketplace.noPros": "Nenhum profissional corresponde.",
  "marketplace.neverQuoted": "Nunca responderam",
  "marketplace.allPros": "Todos",
  "marketplace.unverified": "Não verificado",
  "marketplace.col.pro": "Profissional",
  "marketplace.col.skills": "Ofícios",
  "marketplace.col.quotesGiven": "Orçamentos dados",
  "marketplace.col.rating": "Avaliação",
  "marketplace.categories": "Ofícios",
  "marketplace.categoriesSub": "O que as pessoas pedem, e se alguém o sabe fazer.",
  "marketplace.noCategories": "Nenhum ofício a mostrar.",
  "marketplace.noDemand": "Sem procura",
  "marketplace.coverageHint": "A cobertura é a fração de pedidos com pelo menos um orçamento.",
  "marketplace.col.category": "Ofício",
  "marketplace.col.jobs": "Pedidos",
  "marketplace.col.pros": "Profissionais",
  "marketplace.col.coverage": "Cobertura",
  "marketplace.col.uncovered": "Cidades sem profissional",
  "marketplace.reviews": "Avaliações",
  "marketplace.reviewsSub": "O que os clientes dizem dos profissionais.",
  "marketplace.noReviews": "Nenhuma avaliação corresponde.",
  "marketplace.poorOnly": "3 estrelas ou menos",
  "marketplace.allReviews": "Todas",
  "marketplace.averageIs": "Média {average} em {total} avaliações",
  "nav.group.everyone": "Toda a gente",
  "workspace.yours": "Os seus espaços",
  "department.executive": "Direção",
  "department.executive.blurb": "A empresa num relance",
  "department.marketplace": "Operações do mercado",
  "department.marketplace.blurb": "Pedidos, profissionais, categorias",
  "department.support": "Apoio ao cliente",
  "department.support.blurb": "Processos, clientes e profissionais",
  "department.trust": "Confiança e segurança",
  "department.trust.blurb": "Verificações, denúncias, restrições",
  "department.finance": "Finanças",
  "department.finance.blurb": "Comissões, correções, subscrições",
  "department.growth": "Vendas e crescimento",
  "department.growth.blurb": "Angariar profissionais, promoções",
  "department.product": "Produto e engenharia",
  "department.product.blurb": "Incidentes, regras, lançamentos",
  "department.people": "Recursos humanos",
  "department.people.blurb": "Contratação, emprego, salários",
  "department.administration": "Administração e segurança",
  "department.administration.blurb": "Perfis, permissões, registo",
  "department.legal": "Jurídico e conformidade",
  "department.legal.blurb": "Termos, dados, pedidos legais",
  "nav.signedInAs": "Sessão iniciada como",
  "nav.screenCount": "{roles} · {visible} ecrãs de {total}",
  "profile.title": "O seu perfil",
  "profile.open": "Abrir o seu perfil",
  "profile.identity": "Você",
  "profile.email": "E-mail",
  "profile.roles": "Funções",
  "profile.permits": "O que as suas funções permitem",
  "profile.screensOpen": "{visible} de {total} ecrãs abertos",
  "profile.actionCount": "{count} ações disponíveis",
  "profile.noPermissions": "Sem permissões com limite: nada precisa de aprovação.",
  "profile.preferences": "Preferências",
  "profile.emailLabel": "E-mail:",
  "profile.uuidLabel": "UUID:",
  "profile.rolesLabel": "Funções",
  "profile.figure.screens": "ecrãs abertos para si",
  "profile.figure.actions": "ações que pode realizar",
  "profile.figure.ceilings": "com um limite",
  "profile.ceilingHeading": "O máximo que pode fazer sozinho",
  "profile.ceilingHint": "Acima de um limite a ação abre à mesma — a sua tentativa torna-se um pedido de aprovação.",
  "profile.signOutHint": "Terá de entrar novamente.",
  "header.signOut": "Terminar sessão",
  "header.signingOut": "A terminar sessão…",
  "header.language": "Idioma da consola",
  "header.theme": "Tema",
  "header.theme.system": "Sistema",
  "header.theme.light": "Claro",
  "header.theme.dark": "Escuro",
  "header.languageNotSaved": "Apenas nesta sessão: esta escolha ainda não fica guardada na sua conta.",
  "shell.loading": "A carregar a consola…",
  "shell.loadFailed": "Não foi possível carregar o seu perfil",
  "shell.retry": "Tentar novamente",
  "shell.refreshFailed":
    "Não foi possível atualizar as suas permissões. A consola mostra as que foram carregadas às {time}.",
  "shell.noScreens": "Nenhum ecrã está disponível para as suas funções ({roles}). Peça acesso a um responsável.",
  "shell.noRoles": "Ainda não tem nenhum ecrã acessível. Peça uma função a um responsável.",
  "connection.retrying": "Ligação instável · nova tentativa {attempt} de {max}…",

  // ── Screens ──
  "screen.dashboard": "Painel",
  "screen.myRequests": "Os meus pedidos",
  "screen.myTimeOff": "As minhas férias",
  "myTimeOff.sub": "O seu saldo, o que pediu, e quem mais está fora.",
  "myRequests.sub": "O que pediu para poder fazer, e em que ponto está cada resposta.",
  "screen.as.directory": "Diretório",
  "screen.as.market": "Mercado",
  "screen.as.employees": "Funcionários",
  "screen.as.recruiting": "Recrutamento",
  "screen.as.learning": "Formação",
  "screen.as.payroll": "Folha de pagamento",
  "screen.as.money": "Dinheiro",
  "screen.as.workforce": "Quadro de pessoal",
  "screen.as.accounts": "Contas",
  "screen.cases": "Processos",
  "screen.customers": "Clientes e profissionais",
  "screen.verifications": "Verificações",
  "screen.reports": "Denúncias",
  "screen.billing": "Preços e assinaturas",
  "screen.payments": "Pagamentos",
  "screen.features": "Ativações de funcionalidades",
  "screen.userSettings": "Definições do utilizador",
  "settings.title": "Definições do utilizador",
  "settings.tab.app": "Utilizador da app",
  "settings.tab.employee": "Funcionário",
  "settings.col.setting": "Definições",
  "settings.default": "Valor predefinido",
  "settings.setHere": "Definido para esta pessoa",
  "settings.clear": "Limpar",
  "settings.none": "Nenhuma definição corresponde.",
  "screen.actions": "Ações",
  "actions.title": "Ações",
  "actions.col.action": "Ação",
  "actions.effect.read": "Leitura",
  "actions.effect.write": "Escrita",
  "actions.alsoGates": "+{count} outras",
  "actions.col.area": "Área",
  "actions.col.effect": "Efeito",
  "actions.col.audited": "Auditada",
  "actions.col.needs": "Exige",
  "actions.needs.nothing": "Nada",
  "actions.yes": "Sim",
  "actions.no": "Não",
  "actions.surface.system": "Sistema",
  "actions.search": "Procurar uma ação",
  "actions.none": "Nenhuma ação corresponde.",
  "screen.permissions": "Permissões",
  "permissions.title": "Permissões",
  "permissions.tab.role": "Função",
  "permissions.tab.employee": "Funcionário",
  "permissions.col.limit": "Limite",
  "permissions.col.expires": "Expira",
  "permissions.source.staffRole": "Por função",
  "permissions.source.staffMember": "Exceção",
  "permissions.source.allStaff": "Todo o pessoal",
  "permissions.source.default": "Nenhuma",
  "permissions.noLimit": "Sem limite",
  "permissions.featureOff": "Funcionalidade desligada",
  "permissions.approvable": "Pode pedir",
  "permissions.pickRole": "Escolha uma função",
  "audit.kind.featureEnablements": "Ativações de funcionalidades",
  "audit.kind.permissions": "Permissões",
  "audit.kind.teams": "Funções e equipas",
  "audit.kind.actions": "Ações",
  "audit.kind.userSettings": "Definições do utilizador",
  "screen.roles": "Funções",
  "roles.title": "Funções",
  "roles.grants.none": "Ainda não concede nada",
  "roles.grants.permissions": "Alterar as permissões",
  "roles.grants.features": "Alterar as funcionalidades",
  "roles.explain":
    "O que alguém sabe e está habilitado a fazer. Uma função é a mesma em todas as equipas — liderar não é uma, isso define-se em Equipas.",
  "roles.col.name": "Função",
  "roles.col.description": "O que abrange",
  "roles.col.members": "Pessoas",
  "roles.none": "Ainda não há funções.",
  "roles.loading": "A carregar as funções…",
  "roles.loadFailed": "Não foi possível ler as funções.",
  "roles.retry": "Tentar de novo",
  "roles.edit": "Mudar o nome",
  "roles.save": "Guardar",
  "roles.cancel": "Cancelar",
  "roles.created": "Função criada.",
  "roles.new": "Nova função",
  "roles.new.title": "Nova função",
  "roles.back": "← Todas as funções",
  "roles.opens": "O que abre",
  "roles.opens.explain":
    "Os ecrãs que esta função alcança, a partir das marcas à esquerda. Não é uma lista à parte — um ecrã aparece porque a função tem o que é preciso, e a API verifica o mesmo.",
  "roles.new.name": "Nome",
  "roles.new.description": "O que abrange",
  "roles.new.create": "Criar a função",
  "roles.new.nameNeeded": "Dê um nome à função.",
  "roles.new.grantsNeeded": "Escolha para que serve a função: uma função que não concede nada não tem efeito.",
  "roles.new.grants": "O que pode usar",
  "roles.new.permissions": "O que pode fazer",
  "roles.lock.critical": "Só um Owner pode conceder isto.",
  "roles.lock.high": "Só pode conceder o que o próprio tem.",
  "roles.lock.readOnly": "Não pode conceder numa função.",
  "roles.lock.featureFirst": "Ative primeiro aquilo em que assenta.",
  "screen.teams": "Equipas",
  "teams.title": "Equipas",
  "teams.col.name": "Equipa",
  "teams.col.purpose": "Para que serve",
  "teams.col.lead": "Liderada por",
  "teams.col.members": "Pessoas",
  "teams.col.status": "Estado",
  "teams.col.person": "Pessoa",
  "teams.col.roles": "Função",
  "teams.col.onTeam": "Na equipa",
  "teams.col.leads": "Lidera-a",
  "teams.open": "Abrir",
  "teams.back": "Todas as equipas",
  "teams.none": "Ainda não há equipas.",
  "teams.noMembers": "Ainda não há ninguém nesta equipa.",
  "teams.noOthers": "Já toda a gente está nesta equipa.",
  "teams.loading": "A carregar as equipas…",
  "teams.loadFailed": "Não foi possível ler as equipas.",
  "teams.retry": "Tentar de novo",
  "teams.status.open": "Aberta",
  "teams.status.closed": "Fechada",
  "teams.rename": "Mudar o nome",
  "teams.close": "Fechar a equipa",
  "teams.reopen": "Reabrir",
  "teams.closedReason": "Fechada",
  "teams.reopenedReason": "Reaberta",
  "teams.closedNote": "Fechada. Toda a gente continua nela: ao reabrir, ainda lá estão.",
  "teams.new": "Nova equipa",
  "teams.explain":
    "Um grupo de pessoas que faz o mesmo tipo de trabalho, com um responsável. O trabalho vai para a equipa, não para uma pessoa: quem está de serviço hoje pega no seguinte. Uma equipa não concede nada.",
  "teams.new.people":
    "Quem está nela desde que existe. Marque “Lidera-a” para dizer de quem são estas pessoas: redistribui o trabalho da equipa e substitui qualquer um.",
  "teams.new.name": "Nome",
  "teams.new.purpose": "Para que serve",
  "teams.new.create": "Criar a equipa",
  "teams.new.cancel": "Cancelar",
  "teams.new.nameNeeded": "Dê um nome à equipa.",
  "teams.nameTaken": "Já existe uma equipa com esse nome.",
  "teams.created": "Equipa criada.",
  "teams.leadHint": "São as suas pessoas: redistribui o trabalho da equipa e substitui qualquer um.",
  "teams.leadNeedsMember": "Só se pode liderar uma equipa de que se faz parte.",
  "teams.noLead": "Sem responsável",
  "teams.gone": "Quem saiu não pode ser colocado numa equipa.",
  "teams.locked": "Não pode alterar as equipas.",
  "screen.rules": "Regras",
  "screen.hr": "Recursos humanos",
  "sub.hr": "Procure qualquer pessoa. O recrutamento, os processos e tudo o que está abaixo são dos recursos humanos.",
  "screen.team": "Funções e equipa",
  "screen.audit": "Auditorias Walliyo",

  // ── Locked screen (opened by URL without the feature) ──
  "lock.requires": "Este ecrã requer",
  "lock.or": " ou ",
  "lock.roles": "As suas funções ({roles}) não o incluem, por isso não aparece no seu menu.",
  "lock.noRoles": "Não tem nenhuma função, por isso não aparece no seu menu.",

  // ── Sign-in ──
  "signIn.title": "Iniciar sessão na consola",
  "signIn.subtitle": "Reservada à equipa Walliyo. Receberá um código por SMS.",
  "signIn.email": "Email profissional",
  "signIn.password": "Palavra-passe",
  "signIn.submit": "Continuar",
  "signIn.submitting": "A verificar…",
  "verify.title": "Código de verificação",
  "verify.subtitle": "Enviámos um código por SMS para {phone}.",
  "verify.code": "Código recebido por SMS",
  "verify.submit": "Iniciar sessão",
  "verify.submitting": "A verificar…",
  "verify.expiresIn": "O código expira dentro de {time}.",
  "verify.expired": "O código expirou. Inicie sessão novamente para receber um novo.",
  "verify.restart": "Iniciar sessão novamente",

  // ── Errors: what failed and what to do (ADR-0016 §2.5) ──
  "error.requestId": "Identificador do pedido: {id}",
  "error.noRequestId": "Sem identificador do pedido: o servidor não respondeu.",
  "error.signIn.unauthenticated": "Email ou palavra-passe incorretos. Verifique-os e tente novamente.",
  "error.signIn.validation_error": "Introduza um email e uma palavra-passe válidos.",
  "error.verify.validation_error": "Introduza o código de 6 dígitos recebido por SMS.",
  "error.verify.session_expired": "O código expirou. Inicie sessão novamente para receber um novo.",
  "error.verify.unauthenticated": "Este pedido de código já não é válido. Inicie sessão novamente.",
  "error.unauthenticated": "A sua sessão já não está iniciada. Inicie sessão novamente.",
  "error.session_expired": "A sua sessão expirou. Inicie sessão novamente.",
  "error.feature_not_enabled": "As suas funções não permitem esta ação. Peça acesso a um responsável.",
  "error.invalid_code": "Código incorreto. Verifique o SMS e introduza novamente o código.",
  "error.too_many_attempts": "Demasiadas tentativas. Aguarde alguns minutos e inicie sessão novamente.",
  "error.stale_version": "Isto foi alterado enquanto o consultava. Recarregue para ver a versão atual.",
  "error.validation_error": "Alguns dados são inválidos. Corrija-os e tente novamente.",
  "error.internal_error":
    "O servidor encontrou um erro. Tente novamente; se continuar, envie o identificador abaixo à equipa técnica.",
  "error.network_error": "Sem resposta do servidor após várias tentativas. Verifique a sua ligação e tente novamente.",
  "error.network_error.write":
    "Sem resposta do servidor: o pedido pode ter sido processado. Tente novamente, não será feito duas vezes.",
  "error.unexpected_response":
    "Resposta inesperada do servidor. Tente novamente; se continuar, envie o identificador abaixo à equipa técnica.",
  "error.unknown": "Ocorreu um erro inesperado na consola. Recarregue a página; se continuar, avise a equipa técnica.",

  "sub.cases": "Fila partilhada · chamadas, emails e chat no mesmo lugar",

  "sub.customers": "Pesquisar um cliente ou profissional e ver toda a sua conta",

  "sub.verifications": "Verificar a identidade dos profissionais antes de poderem ser encontrados",

  "sub.reports": "Denúncias sobre profissionais ou clientes, por gravidade",

  "sub.billing": "Preços por país, promoções e descontos · o profissional mantém o preço com que assinou",
  "sub.payments": "Taxas dos profissionais · Orange Money e cartão",

  "sub.features": "Ativar ou desativar por nível: todos, uma região, uma versão, uma pessoa",

  "sub.rules": "Valores da empresa · cada alteração cria uma versão",

  // ── Fonctionnalités ──
  "features.title": "Ativações de funcionalidades",
  "features.loading": "A carregar as funcionalidades…",
  "features.loadFailed": "Não foi possível carregar as funcionalidades.",
  "features.retry": "Tentar novamente",
  "features.close": "Fechar",
  "features.pick": "Escolha uma funcionalidade para ver as exceções.",
  "features.notFound": "Esta funcionalidade não existe ou já não existe. Escolha uma da lista.",
  "features.subject.for": "Registo",
  "features.subject.allClients": "Todos os clientes",
  "features.subject.allPros": "Todos os profissionais",
  "features.subject.user": "Utilizador",
  "features.subject.uuid": "UUID",
  "features.subject.uuidHint": "31290388",
  "features.subject.unknown": "UUID não encontrado.",
  "features.kind.client": "Cliente",
  "features.kind.pro": "Profissional",
  "features.col.source": "Origem",
  "features.col.value": "Valor",
  "features.col.category": "Categoria",
  "features.value.on": "Ativado",
  "features.value.off": "Desativado",
  "features.source.default": "Predefinição",
  "features.source.thisUser": "Este utilizador",
  "features.source.user": "Um utilizador",
  "features.source.allClients": "Todos os clientes",
  "features.source.allPros": "Todos os profissionais",
  "features.source.proProfile": "Perfil profissional",
  "features.source.region": "Região",
  "features.source.appVersion": "Versão da app",
  "features.source.system": "Sistema",
  "features.locked.owner": "Só um Owner pode alterar",
  "features.locked.scope": "Não definível neste nível",
  "features.save.discard": "Descartar",
  "features.save.sending": "A guardar…",
  "features.save.nothing": "Nada para guardar: nenhuma alteração.",
  "features.save.locked": "Uma das alterações não lhe compete.",
  "features.save.reasonNeeded": "Diga porquê, em poucas palavras.",
  "features.save.leave": "Tem alterações por guardar.",
  "features.clear": "Voltar ao valor herdado",
  "features.cleared": "Será apagado",
  "features.filter.any": "Todas",
  "features.filter.own": "Definida aqui",
  "features.filter.inherited": "Herdada",
  "features.filter.search": "Pesquisar",
  "features.filter.none": "Nenhuma funcionalidade corresponde a estes filtros.",
  "features.surface.app": "App Walliyo",
  "features.surface.console": "Consola Walliyo",
  "features.save.title.one": "1 alteração por guardar",
  "features.save.title.many": "{count} alterações por guardar",
  "features.save.submit.one": "Guardar 1 alteração",
  "features.save.submit.many": "Guardar {count} alterações",
  "features.save.done.one": "1 alteração guardada.",
  "features.save.done.many": "{count} alterações guardadas.",
  "features.unsaved": "por guardar",
  "features.surface.consoleSoon":
    "As {count} funcionalidades da consola ainda só se definem por função. Por pessoa chega aqui.",
  "features.surface.consoleWhere": "Abrir Funções e equipa",
  "features.subject.isStaff":
    "Esse número pertence a um funcionário Walliyo. As funcionalidades dele estão no separador Consola Walliyo.",
  "features.subject.search": "Pesquisar",
  "features.subject.searching": "A pesquisar…",
  "features.subject.needsUuid": "Escreva os oito dígitos do UUID.",
  "features.subject.pickFirst": "Escolha para quem e depois pesquise.",
  "features.subject.stale": "Estes resultados são da pesquisa anterior. Pesquise de novo para os atualizar.",
  "features.pill.allClients": "Todos os registos de cliente",
  "features.pill.allPros": "Todos os registos de profissional",
  "features.pill.unknown": "Utilizador desconhecido",
  "features.pill.staff": "Funcionário Walliyo",
  "features.subject.allStaff": "Todo o pessoal",
  "features.subject.staffMember": "Funcionário",
  "features.staffSource.override": "Exceção",
  "features.staffSource.byRole": "Por função",
  "features.staffSource.byRoleNamed": "Por função: {roles}",
  "features.staffSource.allStaff": "Todo o pessoal",
  "features.locked.ownAccount": "Não pode alterar a sua própria conta",
  "features.locked.grant": "Não pode conceder esta",
  "features.pill.allStaff": "Todos os registos de pessoal",
  "features.pill.unknownStaff": "Funcionário desconhecido",
  "features.subject.isCustomer":
    "Esse número pertence a um cliente, não a um funcionário. As funcionalidades dele estão no separador App Walliyo.",
  "features.subject.employeeUuid": "UUID do funcionário",
  "features.subject.staffUuidHint": "27217102",
  "features.col.feature": "Funcionalidade",
  "features.col.default": "Predefinição",
  "features.col.exceptions": "Exceções",
  "features.for.clients": "Para clientes",
  "features.for.pros": "Para profissionais",
  "features.for.everyone": "Para todos",
  "features.on": "Ativada",
  "features.off": "Desativada",
  "features.planned": "Prevista",
  "features.planned.note": "Prevista: pode definir um valor, mas nenhum código o lê ainda.",
  "features.detail.default": "Predefinição: {value}",
  "features.sensitivity.open":
    "Sensibilidade {level}: qualquer pessoa que gere as funcionalidades da app pode alterá-la.",
  "features.sensitivity.critical":
    "Sensibilidade crítica: só um proprietário a pode alterar, e os proprietários são avisados.",
  "features.locked.critical":
    "Bloqueada: esta funcionalidade é crítica e só um proprietário a pode alterar. As suas funções ({roles}) não fazem de si proprietário; o servidor também recusaria.",
  "features.exceptions.title": "Exceções por nível",
  "features.col.scope": "Nível",
  "features.col.target": "Alvo",
  "features.col.reason": "Motivo",
  "features.col.expires": "Expira",
  "features.col.actions": "Gerir",
  "features.default.scope": "Predefinição",
  "features.default.reason": "valor predefinido",
  "features.exceptions.none": "Sem exceções: a predefinição aplica-se a todos.",
  "features.expired": "Expirada",
  "features.expires.never": "Até ser alterada",
  "features.edit": "Alterar",
  "features.remove": "Remover",
  "features.add": "Adicionar uma exceção",
  "features.scope.system": "Sistema",
  "features.scope.allClients": "Todos os clientes",
  "features.scope.allPros": "Todos os profissionais",
  "features.scope.region": "Região",
  "features.scope.appVersion": "Versão da app",
  "features.scope.user": "Uma pessoa",
  "features.scope.proProfile": "Um perfil de profissional",
  "features.scope.clientProfile": "Um perfil de cliente",
  "features.target.everyone": "Todos",
  "features.target.allClients": "Todos os clientes",
  "features.target.allPros": "Todos os profissionais",
  "features.target.appVersion": "Versão {version} e mais recentes",
  "features.target.unnamed": "Conta sem nome",
  "features.form.addTitle": "Nova exceção",
  "features.form.editTitle": "Alterar a exceção",
  "features.form.scope": "Nível",
  "features.form.scopePick": "Escolher um nível…",
  "features.form.scopeUnsupported": "{scope} (ainda não é possível)",
  "features.form.clientProfileNote":
    "Um perfil de cliente ainda não pode receber um valor: os perfis de cliente ainda não existem. Para um cliente, escolha «Uma pessoa».",
  "features.form.needsSearch": "Escolher uma pessoa ou um perfil de profissional requer",
  "features.form.needsView": "Encontrar o perfil de profissional de uma conta requer",
  "features.form.region": "Região",
  "features.form.regionPick": "Escolher uma região…",
  "features.form.appVersion": "A partir da versão",
  "features.form.appVersionHint": "Por exemplo 2.4.0: aplica-se a essa versão e às mais recentes.",
  "features.form.person": "Pessoa",
  "features.form.pro": "Perfil de profissional",
  "features.form.search": "Pesquisar",
  "features.form.searching": "A pesquisar…",
  "features.form.searchPlaceholder": "Nome, telefone, email ou ID da conta",
  "features.form.noResults": "Nenhuma conta corresponde.",
  "features.form.pickedChange": "Mudar",
  "features.form.value": "Valor",
  "features.form.reason": "Motivo (obrigatório)",
  "features.form.reasonHint": "Fica registado no registo de auditoria com a alteração.",
  "features.form.expires": "Expira em",
  "features.form.expiresHint": "Opcional. Vazio: até ser alterada.",
  "features.form.version": "Baseada na versão {version} desta exceção.",
  "features.form.submitAdd": "Adicionar a exceção",
  "features.form.submitEdit": "Guardar",
  "features.form.cancel": "Cancelar",
  "features.form.sending": "A enviar…",
  "features.problem.scope": "Escolha um dos níveis propostos.",
  "features.problem.region": "Escolha uma região.",
  "features.problem.appVersion": "Introduza uma versão como 2.4.0.",
  "features.problem.person": "Pesquise a pessoa e escolha-a nos resultados.",
  "features.problem.pro": "Pesquise o profissional e escolha uma conta com perfil de profissional.",
  "features.problem.exists": "Já existe uma exceção para este alvo: altere-a em vez disso.",
  "features.problem.reason": "Indique um motivo em poucas palavras.",
  "features.problem.expiryInvalid": "Esta data de expiração não é válida.",
  "features.problem.expiryPast": "A data de expiração tem de ser no futuro, ou deixe-a vazia.",
  "features.refused.critical": "Recusado: esta funcionalidade é crítica, só um proprietário a pode alterar.",
  "features.refused.scopeNotSupported":
    "Recusado: um perfil de cliente ainda não pode receber um valor, os perfis de cliente ainda não existem. Escolha «Uma pessoa».",
  "features.refused.scopeNotAllowed":
    "Recusado: esta funcionalidade não pode ser definida a esse nível. Escolha um dos níveis propostos.",
  "features.refused.expiryInPast":
    "Recusado: a data de expiração já passou. Escolha uma data posterior, ou deixe-a vazia.",
  "features.refused.targetNotFound":
    "Recusado: essa conta ou perfil de profissional já não existe. Pesquise-a novamente.",
  "features.stale":
    "Alguém alterou esta funcionalidade entretanto. Recarregue para ver as exceções atuais e tente novamente.",
  "features.done.added": "Exceção adicionada.",
  "features.done.saved": "Exceção alterada.",
  "features.done.removed": "Exceção removida: volta a aplicar-se o nível mais amplo, ou a predefinição.",
  "features.remove.title": "Remover a exceção",
  "features.remove.body":
    "Remover a exceção «{scope} · {target}: {value}» de {feature}. Volta a aplicar-se o nível mais amplo, ou a predefinição.",
  "features.remove.submit": "Remover",

  // ── Règles ──
  "rules.loading": "A carregar as regras…",
  "rules.loadFailed": "Não foi possível carregar as regras.",
  "rules.retry": "Tentar novamente",
  "rules.pick": "Escolha uma regra para ver o histórico.",
  "rules.notFound": "Esta regra não existe. Escolha uma da lista.",
  "rules.next": "a partir de {date}: {value}",
  "rules.now": "Em vigor",
  "rules.default": "valor predefinido",
  "rules.kind.gnf": "montante em GNF",
  "rules.kind.minutes": "duração em minutos",
  "rules.kind.hours": "duração em horas",
  "rules.kind.days": "duração em dias",
  "rules.kind.km": "distância em km",
  "rules.kind.number": "número",
  "rules.kind.boolean": "sim ou não",
  "rules.kind.choice": "escolha numa lista",
  "rules.kind.text": "texto",
  "rules.sensitivity.open": "Sensibilidade {level}: qualquer pessoa que possa alterar as regras pode criar uma versão.",
  "rules.sensitivity.critical": "Sensibilidade crítica: só um proprietário pode criar uma versão.",
  "rules.legalBasis.required": "Cada alteração tem de citar a sua base legal.",
  "rules.locked.critical":
    "Bloqueada: esta regra é crítica, só um proprietário pode criar uma versão. As suas funções ({roles}) não fazem de si proprietário; o servidor também recusaria.",
  "rules.locked.retired": "Esta regra foi retirada: já nada a lê, por isso não pode mudar.",
  "rules.history": "Histórico de versões",
  "rules.history.none": "Nenhuma versão: aplica-se a predefinição ({value}).",
  "rules.col.version": "N.º da versão",
  "rules.col.value": "Valor",
  "rules.col.effective": "Em vigor",
  "rules.col.reason": "Motivo",
  "rules.col.legalBasis": "Base legal",
  "rules.col.by": "Por",
  "rules.status.scheduled": "A partir de {date}",
  "rules.status.current": "Atual",
  "rules.status.since": "desde {date}",
  "rules.status.past": "Substituída",
  "rules.new": "Nova versão",
  "rules.form.title": "Nova versão",
  "rules.form.value": "Novo valor",
  "rules.form.value.gnf": "Novo montante (GNF)",
  "rules.form.value.minutes": "Novo valor (em minutos)",
  "rules.form.value.hours": "Novo valor (em horas)",
  "rules.form.value.days": "Novo valor (em dias)",
  "rules.form.effective": "Entrada em vigor",
  "rules.form.now": "Já",
  "rules.form.at": "Numa data e hora",
  "rules.form.atLabel": "Em vigor a",
  "rules.form.reason": "Motivo (obrigatório)",
  "rules.form.legalBasis": "Base legal (obrigatória)",
  "rules.form.legalBasisOptional": "Base legal (se aplicável)",
  "rules.form.legalBasisPlaceholder": "Lei, contrato, parecer jurídico",
  "rules.form.version":
    "Baseada na versão {version} da regra: se entretanto for adicionada outra versão, o envio será recusado.",
  "rules.form.submit": "Criar a versão",
  "rules.form.cancel": "Cancelar",
  "rules.form.sending": "A enviar…",
  "rules.value.true": "Sim",
  "rules.value.false": "Não",
  "rules.problem.value": "Introduza um valor.",
  "rules.problem.number": "Introduza um número, por exemplo 750000.",
  "rules.problem.integer": "Introduza um número inteiro.",
  "rules.problem.min": "O valor tem de ser pelo menos {min}.",
  "rules.problem.max": "O valor tem de ser no máximo {max}.",
  "rules.problem.choice": "Escolha um valor da lista.",
  "rules.problem.tooLong": "No máximo {max} caracteres.",
  "rules.problem.effectiveMissing": "Escolha a data e a hora de entrada em vigor.",
  "rules.problem.effectivePast":
    "Uma versão não pode entrar em vigor no passado: o que se aplicava então fica registado. Escolha agora ou mais tarde.",
  "rules.problem.reason": "Indique um motivo em poucas palavras.",
  "rules.problem.legalBasis": "Cite a lei, o contrato ou o parecer jurídico que justifica esta alteração.",
  "rules.refused.critical": "Recusado: esta regra é crítica, só um proprietário pode criar uma versão.",
  "rules.refused.effectivePast": "Recusado: essa data já passou. Escolha agora ou mais tarde.",
  "rules.refused.legalBasis":
    "Recusado: esta regra exige uma base legal. Cite a lei, o contrato ou o parecer jurídico.",
  "rules.refused.invalidValue": "Recusado: esse valor não serve para a regra. Verifique a unidade e o mínimo.",
  "rules.refused.retired": "Recusado: esta regra foi retirada, já nada a lê.",
  "rules.stale": "Entretanto foi adicionada outra versão desta regra. Recarregue para a ver e tente novamente.",
  "rules.done.now": "Versão {version} criada: aplica-se já.",
  "rules.done.scheduled": "Versão {version} criada: aplica-se a partir de {date}.",

  "sub.team": "Cada função é uma lista de funcionalidades. Ninguém pode atribuir nada a si próprio.",

  "sub.audit": "Tudo o que foi feito, por quem, para quem · a sua consulta também fica registada",

  // ── Audit log ──
  "audit.filter.subject": "Pessoa em causa",
  "audit.filter.subjectPlaceholder": "Identificador da conta",
  "audit.filter.staff": "Membro da equipa",
  "audit.filter.staffPlaceholder": "Identificador do membro",
  "audit.filter.action": "Ação",
  "audit.filter.area": "Área",
  "audit.filter.outcome": "Resultado",
  "audit.filter.from": "De",
  "audit.filter.to": "Até (inclusive)",
  "audit.filter.all": "Todos",
  "audit.filter.allActions": "Todas",
  "audit.filter.clear": "Limpar filtros",
  "audit.problem.id": "Um identificador tem a forma 3f2a…-…-…-…-… (UUID). Copie-o da ficha da conta.",
  "audit.problem.period": "O fim do período é anterior ao seu início.",
  "audit.problem.needsPerson": "Indique o UUID de quem alterou, o da pessoa, ou ambos.",
  "audit.filtersAria": "Filtros do registo",
  "audit.searchAria": "Pesquisa no registo",
  "audit.scope.user": "Indivíduo",
  "audit.scope.allStaff": "Todo o pessoal",
  "audit.scope.allClients": "Todos os clientes",
  "audit.scope.allPros": "Todos os profissionais",
  "audit.scope.staffRole": "Função",
  "audit.scope.proProfile": "Perfil profissional",
  "audit.scope.region": "Região",
  "audit.scope.appVersion": "Versão da app",
  "audit.scope.system": "Sistema",
  "audit.scope.staffMember": "Indivíduo",
  "audit.filter.involving": "Envolvendo",
  "audit.filter.updaterUuid": "UUID de quem alterou",
  "audit.filter.userUuid": "UUID da pessoa",
  "audit.pill.updater": "Autor",
  "audit.pill.user": "Pessoa",
  "audit.col.time": "Hora",
  "audit.col.actor": "Autor",
  "audit.col.action": "Definição",
  "audit.col.subject": "Refere-se a",
  "audit.col.outcome": "Resultado",
  "audit.col.reason": "Motivo",
  "audit.filter.effect": "Mostrar",
  "audit.effect.write": "Alterações",
  "audit.effect.read": "Consultas",
  "audit.col.before": "Antes",
  "audit.col.after": "Depois",
  "audit.change.arrow": "{before} → {after}",
  "audit.change.field": "{key}: {value}",
  "audit.outcome.succeeded": "Concluído",
  "audit.outcome.denied": "Recusado",
  "audit.outcome.requested": "Solicitado",
  "audit.actor.staff": "Membro da equipa",
  "audit.actor.noRoles": "nenhuma função",
  "audit.actor.customer": "cliente",
  "audit.actor.self": "cliente (o próprio)",
  "audit.actor.system": "Sistema",
  "audit.subject.onBehalf": "em nome de {id}",
  "audit.filterBy": "Filtrar por {id}",
  "audit.subject.open": "Abrir a conta de {name}",
  "audit.masked": "oculto",
  "audit.sensitive": "🔒 sensível",
  "audit.loading": "A carregar o registo…",
  "audit.loadFailed": "Não foi possível carregar o registo.",
  "audit.retry": "Tentar novamente",
  "audit.empty": "Nenhuma entrada corresponde a estes filtros.",
  "audit.emptyForPerson": "Nenhuma entrada para esta pesquisa.",
  "audit.fixProblems": "Corrija os filtros assinalados para mostrar o registo.",
  "audit.loadMore": "Carregar mais",
  "audit.loadingMore": "A carregar…",
  "audit.moreFailed": "Não foi possível carregar a página seguinte. As entradas já mostradas continuam válidas.",
  "audit.count": "{count} entradas mostradas",
  "audit.countOne": "1 entrada mostrada",
  "audit.end": "Fim do registo para estes filtros.",
  "audit.recorded": "A sua consulta também fica registada (VIEW_AUDIT_LOG_STAFF_ACTION), com os filtros usados.",
  "audit.sensitiveShown": "Está a ver os valores sensíveis (VIEW_SENSITIVE_AUDIT_VALUES).",
  "audit.sensitiveHidden":
    "Os valores sensíveis estão ocultos: é necessário VIEW_SENSITIVE_AUDIT_VALUES, concedido apenas pelo proprietário.",
  "audit.deniedNote": "Uma linha «Recusado» mostra que uma tentativa sem permissão também fica registada.",
  "area.account": "Conta",
  "area.access": "Acesso",
  "area.onboarding": "Registo de profissional",
  "area.discovery": "Pesquisa",
  "area.jobs": "Pedidos",
  "area.leads": "Oportunidades",
  "area.messaging": "Mensagens",
  "area.reviews": "Avaliações",
  "area.payments": "Pagamentos",
  "area.support": "Processos",
  "area.safety": "Segurança",
  "area.settings": "Definições",
  "area.staff": "Equipa",
  "area.audit": "Auditoria",

  // ── Roles & team ──
  "team.members": "Equipa",
  "team.col.name": "Nome",
  "team.col.roles": "Funções",
  "team.col.status": "Estado",
  "team.col.lastSeen": "Última sessão",
  "team.you": "você",
  "team.noRoles": "Nenhuma função",
  "team.status.active": "Ativo",
  "team.status.invited": "Convidado",
  "team.status.deactivated": "Desativado",
  "team.sessions": "{count} sessões abertas",
  "team.sessionsOne": "1 sessão aberta",
  "team.lastSeen.never": "Nunca iniciou sessão",
  "team.lastSeen.today": "hoje às {time}",
  "team.lastSeen.yesterday": "ontem às {time}",
  "team.lastSeen.date": "a {date}",
  "team.invite": "Convidar",
  "team.manageRoles": "Funções",
  "team.endSessions": "Terminar a sessão",
  "team.deactivate": "Desativar",
  "team.loading": "A carregar a equipa…",
  "team.loadFailed": "Não foi possível carregar a equipa.",
  "team.retry": "Tentar novamente",
  "team.emptyStaff": "Ainda não há ninguém na equipa.",
  "team.grid": "Funções × funcionalidades",
  "team.grid.feature": "Funcionalidade",
  "team.grid.editable": "Clique para conceder ou retirar · «critical» exige o proprietário",
  "team.grid.readOnly": "🔒 Só de leitura · para alterar é necessário MANAGE_ROLES ou ASSIGN_ROLES",
  "team.grid.members": "{count} membros",
  "team.grid.membersOne": "1 membro",
  "team.grid.yourRole": "🔒 a sua função",
  "team.grid.newRole": "Nova função",
  "team.grid.editRole": "Alterar",
  "team.grid.noRoles": "Ainda não existe nenhuma função.",
  "team.lock.ownRole": "Tem esta função: ninguém altera o que a sua própria função concede.",
  "team.lock.critical": "Crítica: só o proprietário a pode conceder ou retirar.",
  "team.lock.high": "Sensibilidade alta: é necessário MANAGE_ROLES e ter esta funcionalidade.",
  "team.lock.low": "É necessário MANAGE_ROLES ou ASSIGN_ROLES.",
  "team.lock.criticalShort": "🔒 proprietário",
  "team.lock.highShort": "🔒 fora das suas permissões",
  "team.cell.grant": "Conceder {feature} à função {role}",
  "team.cell.revoke": "Retirar {feature} da função {role}",
  "team.sensitivity.low": "low",
  "team.sensitivity.high": "high",
  "team.sensitivity.critical": "critical",

  "team.reason": "Motivo",
  "team.reasonHint": "Fica registado no registo de auditoria. Bastam algumas palavras.",
  "team.reasonTooShort": "Indique um motivo com pelo menos 3 caracteres.",
  "team.cancel": "Cancelar",
  "team.close": "Fechar",
  "team.sending": "A enviar…",
  "team.reload": "Recarregar",
  "team.refused": "Recusado: {message}",
  "team.stale": "Alguém alterou isto entretanto. Recarregue para ver a versão atual e tente novamente.",

  "team.grant.title": "Conceder uma funcionalidade",
  "team.revoke.title": "Retirar uma funcionalidade",
  "team.grant.body": "Conceder {feature} à função {role} ({members}).",
  "team.revoke.body": "Retirar {feature} da função {role} ({members}).",
  "team.grant.submit": "Conceder",
  "team.revoke.submit": "Retirar",
  "team.grant.done": "{feature} concedida à função {role}.",
  "team.revoke.done": "{feature} retirada da função {role}.",
  "team.critical.note": "Funcionalidade crítica: os proprietários serão avisados.",

  "team.invite.title": "Convidar um membro da equipa",
  "team.invite.name": "Nome completo",
  "team.invite.email": "Email profissional",
  "team.invite.phone": "Telefone",
  "team.invite.phoneHint": "Formato internacional, p. ex. +224621234567.",
  "team.invite.roles": "Funções",
  "team.invite.noRoles": "Crie primeiro uma função — pode convidar agora e atribuir funções depois.",
  "team.invite.rolesHint": "Sem função, abrirá uma consola onde nada está acessível.",
  "team.invite.sms":
    "Será enviado um código de 6 dígitos por SMS para este número. Válido durante {hours} horas, permite escolher a palavra-passe na página «Escolher a minha palavra-passe».",
  "team.invite.submit": "Enviar o convite",
  "team.invite.done": "{name} foi convidado. Código enviado por SMS, válido até {until}.",
  "team.invite.missing": "Preencha o nome, o email e o telefone.",

  "team.deactivate.title": "Desativar {name}",
  "team.deactivate.body":
    "{name} nunca mais poderá iniciar sessão. As sessões abertas e o eventual convite terminam imediatamente. Um membro desativado não é eliminado.",
  "team.deactivate.submit": "Desativar",
  "team.deactivate.done": "{name} foi desativado. Sessões terminadas: {count}.",
  "team.reactivate": "Reativar",
  "team.reactivate.title": "Reativar {name}",
  "team.reactivate.body":
    "{name} poderá iniciar sessão novamente, com as funções que já tinha. A autenticação recomeça do zero: nenhuma sessão nem código é restaurado.",
  "team.reactivate.submit": "Reativar",
  "team.reactivate.done": "{name} está ativo novamente.",

  "team.endSessions.title": "Terminar as sessões de {name}",
  "team.endSessions.body": "{name} terá a sessão terminada em todo o lado ({sessions}).",
  "team.endSessions.self": "São as suas próprias sessões: a sua sessão também será terminada.",
  "team.endSessions.submit": "Terminar",
  "team.endSessions.done": "Sessões de {name} terminadas: {count}.",

  "team.roles.title": "Funções de {name}",
  "team.roles.held": "Funções atuais",
  "team.roles.available": "Outras funções",
  "team.roles.none": "Nenhuma",
  "team.roles.assign": "Atribuir {role}",
  "team.roles.remove": "Retirar {role}",
  "team.roles.assignDone": "{role} atribuída a {name}.",
  "team.roles.removeDone": "{role} retirada a {name}.",
  "team.roles.reasonFirst": "Indique primeiro um motivo e depois escolha a função a atribuir ou retirar.",

  "team.role.createTitle": "Nova função",
  "team.role.editTitle": "Alterar a função {name}",
  "team.role.name": "Nome da função",
  "team.role.description": "Descrição",
  "team.role.descriptionHint": "Opcional. O que esta função faz, numa frase.",
  "team.role.department": "Departamento",
  "team.role.noDepartment": "Nenhum",
  "team.role.departmentHint":
    "O espaço onde chegam os seus titulares. O que o perfil permite continua a ser decidido pelas funcionalidades.",
  "team.role.create": "Criar a função",
  "team.role.save": "Guardar",
  "team.role.created": "Função {name} criada. Conceda-lhe funcionalidades na grelha.",
  "team.role.saved": "Função {name} guardada.",
  "team.role.nameTooShort": "O nome tem pelo menos 2 caracteres.",
  "team.role.unchanged": "Altere o nome ou a descrição.",
  "team.role.version": "Versão {version}",

  // ── Permissions on a role (ADR-0019) and who can do this ──
  "team.tab.roles": "Funções e equipa",
  "team.tab.who": "Quem pode fazer isto",
  "team.tabs.aria": "Secções da equipa",
  "team.permission.noLimit": "Sem limite",
  "team.permission.lockFeature":
    "Conceda primeiro {feature} a {role}: uma permissão só conta sob a sua funcionalidade.",
  "team.cell.grantPermission": "Conceder {permission} à função {role}",
  "team.cell.editPermission": "Alterar {permission} para a função {role}",
  "team.permission.grantTitle": "Conceder uma permissão",
  "team.permission.editTitle": "Alterar uma permissão",
  "team.permission.grantBody": "Conceder {permission} à função {role} ({members}).",
  "team.permission.editBody": "A função {role} ({members}) tem {permission}.",
  "team.permission.under": "Sob {feature}.",
  "team.permission.approvable": "Sem ela, a tentativa torna-se um pedido de aprovação.",
  "team.permission.refusable": "Sem ela, a ação é recusada.",
  "team.permission.limits": "Limites",
  "team.permission.limitsHint":
    "O máximo que os seus titulares podem fazer sozinhos em cada moeda. Acima disso, ou numa moeda sem limite, torna-se um pedido de aprovação.",
  "team.permission.noLimitHint": "Sem limite: os seus titulares agem sozinhos, seja qual for o montante.",
  "team.permission.currency": "Moeda",
  "team.permission.amount": "Montante",
  "team.permission.addLimit": "Adicionar um limite noutra moeda",
  "team.permission.removeLimit": "Remover o limite em {currency}",
  "team.permission.limitProblem": "Indique uma moeda de três letras e um montante acima de zero para cada limite.",
  "team.permission.duplicateCurrency": "Um limite por moeda: {currency} aparece duas vezes.",
  "team.permission.grantDone": "{permission} concedida à função {role}.",
  "team.permission.revokeDone": "{permission} retirada à função {role}.",
  "team.revoke.permissionsToo": "As permissões sob ela vão junto: {permissions}.",
  "team.refused.sensitivity":
    "O seu acesso não permite conceder nem retirar isto. Peça a um Ops Manager ou ao proprietário.",
  "team.refused.critical": "Crítico: só um proprietário que o tenha o concede ou retira.",
  "team.refused.limitAboveOwn": "Não pode conceder um limite superior ao seu. Peça ao proprietário.",
  "team.refused.featureFirst":
    "Esta permissão assenta numa funcionalidade que a função não concede. Conceda primeiro a funcionalidade.",
  "team.refused.notLimited": "Esta permissão não tem limite.",
  "team.refused.limitWithoutGrant": "Um limite vai com uma permissão concedida.",
  "team.refused.ownRole": "Tem esta função, por isso não pode alterar o que ela concede. Peça a outro proprietário.",
  "team.who.action": "Ação",
  "team.who.feature": "Funcionalidade",
  "team.who.permission": "Permissão",
  "team.who.noPermission": "Nenhuma permissão cobre esta ação: quem tem a funcionalidade pode executá-la.",
  "team.who.approvable": "pode ser pedida",
  "team.who.col.through": "Através de",
  "team.who.col.limit": "Limite",
  "team.who.personal": "Definido para esta pessoa",
  "team.who.mayRequest": "Só pode pedir: torna-se um pedido de aprovação",
  "team.who.loading": "A carregar quem pode fazer isto…",
  "team.who.loadFailed": "Não foi possível carregar quem pode fazer isto.",
  "team.who.empty": "Ninguém pode fazer isto.",

  // ── Customers & pros ──
  // Trades, by WorkerCategory.
  "trade.electrician": "Eletricista",
  "trade.plumber": "Canalizador",
  "trade.mason": "Pedreiro",
  "trade.carpenter": "Carpinteiro",
  "trade.ac_technician": "Técnico de ar condicionado",
  "trade.cleaner": "Empregada de limpeza",
  "trade.painter": "Pintor",
  "trade.handyman": "Faz-tudo",
  "trade.laundry": "Lavandaria",
  "trade.babysitter": "Babysitter",
  "trade.fridge_repair": "Reparador de frigoríficos",
  "trade.moto_driver": "Mototáxi",
  "trade.tailor": "Alfaiate",
  "trade.car_mechanic": "Mecânico auto",
  "trade.welder": "Soldador",
  "trade.gardener": "Jardineiro",
  "trade.phone_repair": "Reparador de telemóveis",
  "trade.tutor": "Explicador",
  "trade.hairdresser": "Cabeleireiro",
  "trade.security_guard": "Guarda",
  "trade.other": "Outro",
  // A number with its unit (the settings' Unit).
  "unit.km": "{value} km",
  "unit.minutes": "{value} min",
  "unit.hours": "{value} h",
  "unit.days": "{value} d",
  "unit.seconds": "{value} s",

  "customers.search.label": "Pesquisar um cliente ou profissional",
  "customers.search.placeholder": "Nome, telefone, email ou identificador da conta",
  "customers.search.hint": "P. ex. «621 23 45 67», «Sékou Touré», «mariama.bah@» ou o identificador da conta.",
  "customers.search.submit": "Pesquisar",
  "customers.search.tooShort": "Introduza pelo menos 2 caracteres.",
  "customers.search.locked": "A pesquisa requer",
  "customers.search.idle": "Introduza um nome, um número, um email ou um identificador para encontrar uma conta.",
  "customers.search.loading": "A pesquisar…",
  "customers.search.loadFailed": "Não foi possível concluir a pesquisa.",
  "customers.search.empty": "Nenhuma conta corresponde a «{query}».",
  "customers.search.matchedBy.userId": "Pesquisa por identificador de conta",
  "customers.search.matchedBy.email": "Pesquisa por email",
  "customers.search.matchedBy.phone": "Pesquisa por telefone",
  "customers.search.matchedByPhone": "Pesquisa por telefone: {phone}",
  "customers.search.matchedBy.name": "Pesquisa por nome",
  "customers.search.count": "{count} contas mostradas",
  "customers.search.countOne": "1 conta mostrada",
  "customers.search.end": "Fim dos resultados.",
  "customers.search.loadMore": "Carregar mais",
  "customers.search.loadingMore": "A carregar…",
  "customers.search.moreFailed":
    "Não foi possível carregar a página seguinte. As contas já mostradas continuam válidas.",
  "customers.search.retry": "Tentar novamente",
  "customers.search.masked": "Números e emails ocultos. «Mostrar», na página da conta e com um motivo, fica registado.",
  "customers.col.name": "Nome",
  "customers.col.contact": "Contacto",
  "customers.col.kind": "Cliente ou profissional",
  "customers.col.region": "Região",
  "customers.col.since": "Membro desde",
  "customers.deleted": "Conta eliminada",
  "customers.deletedName": "Nome apagado",
  "customers.kind.client": "Cliente",
  "customers.kind.pro": "Profissional",
  "customers.kind.proAndClient": "Profissional e cliente",
  "customers.kind.admin": "Administrador",
  "customers.region.gn": "Guiné",
  "customers.region.sn": "Senegal",
  "customers.region.us": "Estados Unidos",
  "customers.region.eu": "Europa",
  "customers.verification.unverified": "Não verificada",
  "customers.verification.pending": "Pendente",
  "customers.verification.verified": "Verificada",
  "customers.verification.rejected": "Recusada",

  "customers.back": "← Pesquisa",
  "customers.page.loading": "A carregar a conta…",
  "customers.page.loadFailed": "Não foi possível carregar a conta.",
  "customers.page.notFound": "Nenhuma conta tem este identificador. Pesquise a pessoa novamente.",
  "customers.page.locked": "A página de uma conta requer",
  "customers.page.sub": "{kind} · membro desde {since} · {region}",
  "customers.page.deletedTitle": "Conta eliminada",
  "customers.page.deletedSub": "Eliminada a {date} · nome e contactos apagados",

  "customers.reveal.button": "Mostrar",
  "customers.reveal.locked": "🔒 oculto",
  "customers.reveal.lockedTitle": "Mostrar os contactos requer REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  "customers.reveal.title": "Mostrar os contactos de {name}",
  "customers.reveal.body":
    "O número e o email completos aparecem apenas nesta página, durante a sua consulta. A visualização fica registada no registo da conta, com o seu motivo.",
  "customers.reveal.reason": "Motivo",
  "customers.reveal.placeholder": "Porquê? ex. ligar de volta ao cliente, processo D-0142",
  "customers.reveal.tooShort": "Indique um motivo com pelo menos 3 caracteres.",
  "customers.reveal.submit": "Mostrar",
  "customers.reveal.sending": "A enviar…",
  "customers.reveal.cancel": "Cancelar",
  "customers.reveal.noAnswer":
    "Sem resposta do servidor: a visualização pode ter ficado registada sem que os contactos lhe chegassem. Tente novamente; uma nova visualização também ficará registada.",
  "customers.reveal.recorded": "Mostrado às {time} · consulta registada no registo da conta.",
  "customers.reveal.hide": "Ocultar",
  "customers.reveal.phone": "Telefone:",
  "customers.reveal.email": "Email:",
  "customers.reveal.none": "nenhum",

  "customers.obo.title": "Agir em nome de {name}",
  "customers.obo.body":
    "É enviado um código por SMS para o número da conta. O cliente lê-o em voz alta; a sessão dura 30 minutos e cada ação fica registada com o nome dele e o seu.",
  "customers.obo.send": "Enviar o código",
  "customers.obo.soon": "Disponível em breve",

  "customers.tabs.aria": "Secções da conta",
  "customers.tab.overview": "Resumo",
  "customers.tab.subscription": "Assinatura",
  "customers.tab.settings": "Funcionalidades e definições",
  "customers.tab.jobs": "Pedidos",
  "customers.tab.audit": "Registo",

  "customers.pro.title": "Perfil profissional",
  "customers.pro.none": "Sem perfil profissional.",
  "customers.pro.identity": "Identidade",
  "customers.pro.approval": "Aprovação",
  "customers.pro.rating": "Classificação",
  "customers.pro.ratingValue": "{rating} ★ · {count} avaliações",
  "customers.pro.noReviews": "Nenhuma avaliação",
  "customers.pro.rate": "Tarifa",
  "customers.pro.subscription": "Plano",
  "customers.pro.noSubscription": "Nenhum",
  "customers.pro.subscriptionLine": "{plan} · {status}",
  "customers.pro.paidUntil": "{plan} · pago até {date}",
  "customers.pro.endingUntil": "{plan} · ativa até {date}, sem renovação",
  "customers.pro.approval.pending": "Pendente",
  "customers.pro.approval.approved": "Aprovado",
  "customers.pro.approval.rejected": "Recusado",
  "customers.subscription.pending": "pendente",
  "customers.subscription.active": "ativo",
  "customers.subscription.ending": "sem renovação",
  "customers.subscription.suspended": "suspensa",
  "customers.subscription.ended": "terminada",
  "customers.rate.hourly": "{amount} / hora",
  "customers.rate.daily": "{amount} / dia",
  "customers.rate.fixed": "{amount} preço fixo",
  "customers.client.title": "Perfil de cliente",
  "customers.client.jobsPosted": "Pedidos publicados",
  "customers.client.lastActivity": "Última atividade",
  "customers.client.never": "Nenhuma",
  "customers.restrictions.title": "Restrições ativas",
  "customers.restrictions.none": "Nenhuma restrição.",
  "customers.restrictions.suspension": "Suspensão provisória",
  "customers.restrictions.line": "{feature} desativada para {scope} · motivo: {reason}",
  "customers.restrictions.until": "até {date}",

  "customers.settings.col.setting": "Definição",
  "customers.settings.col.value": "Valor",
  "customers.settings.col.source": "Nível",
  "customers.settings.col.reason": "Motivo",
  "customers.settings.col.expires": "Expira",
  "customers.settings.on": "Ativada",
  "customers.settings.off": "Desativada",
  "customers.settings.restricted": "Restrita",
  "customers.settings.none": "Nenhuma definição se aplica a esta conta.",
  "customers.settings.edit": "Alterar uma definição",
  "customers.settings.editSoon":
    "Disponível em breve: a alteração das definições de um cliente chega numa próxima versão.",
  "customers.settings.foot":
    "Prevalece o nível mais específico: pessoa, perfil profissional, todos os profissionais ou todos os clientes, região, sistema e, por fim, o valor predefinido.",
  "customers.source.user": "Pessoa",
  "customers.source.proProfile": "Perfil profissional",
  "customers.source.allPros": "Todos os profissionais",
  "customers.source.allClients": "Todos os clientes",
  "customers.source.region": "Região",
  "customers.source.regionOf": "Região {region}",
  "customers.source.appVersion": "Versão da app",
  "customers.source.system": "Sistema",
  "customers.source.default": "Predefinição",
  "customers.scope.user": "a pessoa",
  "customers.scope.proProfile": "o perfil profissional",

  "customers.jobs.col.trade": "Ofício",
  "customers.jobs.col.type": "Tipo",
  "customers.jobs.col.side": "Papel",
  "customers.jobs.col.other": "Com",
  "customers.jobs.col.status": "Estado",
  "customers.jobs.col.date": "Data",
  "customers.jobs.type.direct": "Direto",
  "customers.jobs.type.open": "Aberto",
  "customers.jobs.side.client": "Cliente",
  "customers.jobs.side.pro": "Profissional",
  "customers.jobs.status.requested": "Pedido",
  "customers.jobs.status.quoted": "Orçamento enviado",
  "customers.jobs.status.accepted": "Apresentação feita",
  "customers.jobs.status.cancelled": "Cancelado",
  "customers.jobs.noPro": "Nenhum profissional escolhido",
  "customers.jobs.deletedParty": "Conta eliminada",
  "customers.jobs.none": "Nenhum pedido para esta conta.",
  "customers.jobs.foot": "Os últimos {count} pedidos, no máximo, como cliente ou como profissional.",

  "customers.journal.recorded": "A sua consulta deste registo também fica registada.",
  "customers.journal.locked": "O registo de auditoria requer",
  "customers.lockedRoles": "As suas funções ({roles}) não o incluem.",
  "customers.journal.onBehalf": "em nome de {id}",
  "customers.journal.actorWithRoles": "{name} ({roles})",
  "customers.journal.reason": "motivo: {reason}",
  "customers.journal.empty": "Ainda nada foi registado para esta conta.",
  "customers.journal.all": "Abrir no registo de auditoria →",
  "customers.journal.denied": "recusado",
  "customers.journal.succeeded": "concluído",

  // ── Choose my password (public) ──
  "setPassword.title": "Escolher a minha palavra-passe",
  "setPassword.subtitle": "Use o código de 6 dígitos recebido por SMS com o seu convite.",
  "setPassword.email": "Email profissional",
  "setPassword.code": "Código recebido por SMS",
  "setPassword.password": "Nova palavra-passe",
  "setPassword.confirm": "Confirme a palavra-passe",
  "setPassword.rules": "A palavra-passe deve:",
  "setPassword.rule.length": "ter pelo menos {min} caracteres",
  "setPassword.rule.tooLong": "ter no máximo {max} caracteres",
  "setPassword.rule.mix": "conter uma letra e um caráter que não seja uma letra",
  "setPassword.rule.email": "não conter o seu email ({name})",
  "setPassword.rule.emailGeneric": "não conter o seu email",
  "setPassword.mismatch": "As duas palavras-passe não são iguais.",
  "setPassword.codeFormat": "O código tem 6 dígitos.",
  "setPassword.emailFormat": "Introduza um email válido.",
  "setPassword.submit": "Guardar a minha palavra-passe",
  "setPassword.submitting": "A guardar…",
  "setPassword.back": "Voltar ao início de sessão",
  "setPassword.done": "Palavra-passe guardada. Inicie sessão com o seu email e esta palavra-passe.",
  "signIn.setPassword": "Foi convidado? Escolher a minha palavra-passe",
  "signIn.or": "ou",
  "signIn.heading": "Consola Walliyo",
  "signIn.forgot": "Esqueceu-se da palavra-passe?",
  "signIn.environment": "Ambiente",
  "signIn.build": "Compilação",
  "signIn.googleUnavailable":
    "A autenticação Google não está disponível. Use o seu e-mail e palavra-passe, ou avise o proprietário.",
  "error.setPassword.invalid_code":
    "Este email e este código não correspondem a nenhum convite em aberto. Verifique ambos; se o código se perdeu ou expirou, peça para ser convidado novamente.",
  "error.setPassword.too_many_attempts":
    "Demasiados códigos incorretos: este código já não é válido. Peça para ser convidado novamente.",
  "error.setPassword.validation_error":
    "A palavra-passe não cumpre as regras, ou o código não tem o formato correto. Corrija e tente novamente.",

  // ── Dossiers (the support case queue) ──
  "cases.newCall": "+ Registar uma chamada",
  "cases.queue.aria": "Fila de processos",
  "cases.queue.views": "Vistas da fila",
  "cases.view.all": "Todos",
  "cases.view.unassigned": "Não atribuídos",
  "cases.view.mine": "Os meus",
  "cases.view.late": "Em atraso",
  "cases.filter.status": "Estado",
  "cases.filter.notClosed": "Todos exceto fechados",
  "cases.filter.channel": "Canal",
  "cases.filter.anyChannel": "Todos os canais",
  "cases.filter.customer": "Cliente {id}",
  "cases.filter.clear": "Retirar o filtro de cliente",
  "cases.col.case": "Processo",
  "cases.col.deadline": "Prazo",
  "cases.queue.loading": "A carregar a fila…",
  "cases.queue.loadFailed": "Não foi possível carregar a fila.",
  "cases.queue.empty": "Nenhum processo corresponde a estes filtros.",
  "cases.loadMore": "Carregar mais",
  "cases.loadingMore": "A carregar…",
  "cases.retry": "Tentar novamente",
  "cases.pick": "Escolha um processo na fila para ver a sua cronologia.",
  "cases.loading": "A carregar o processo…",
  "cases.loadFailed": "Não foi possível carregar este processo.",
  "cases.notFound": "Nenhum processo tem este identificador. Talvez tenha sido mal copiado.",
  "cases.refreshFailed": "Não foi possível atualizar o processo: o que vê pode estar desatualizado.",
  "cases.deadline.late": "Em atraso · {time}",
  "cases.deadline.left": "faltam {time}",
  "cases.deadline.replied": "Respondido",
  "cases.deadline.closed": "Fechado",
  "cases.channel.phone": "Chamada",
  "cases.channel.email": "E-mail",
  "cases.channel.chat": "Chat na app",
  "cases.status.open": "Aberto",
  "cases.status.waiting_on_customer": "À espera do cliente",
  "cases.status.closed": "Fechado",
  "access.mine.title": "O que pediu",
  "access.mine.none": "Não pediu nada.",
  "access.mine.what": "O quê",
  "access.mine.state": "Em que ponto está",
  "access.mine.asked": "Pedido",
  "access.mine.withWhom": "Com",
  "access.mine.waiting": "À espera",
  "access.mine.granted": "Concedido",
  "access.mine.refused": "Recusado",
  "access.mine.overdue": "Atrasado",
  "access.mine.nobody": "Ainda ninguém",
  "access.mine.answer": "Resposta",
  "access.mine.loading": "A carregar os seus pedidos…",
  "access.mine.loadFailed": "Não foi possível ler os seus pedidos.",
  "access.ask.title": "Pedir mais",
  "access.ask.explain":
    "Construir a coisa não é razão para deter os dados dela. Peça o mínimo que lhe permite acabar, pelo tempo de que precisa. Vai para quem o pode conceder, é concedido só a si, e termina sozinho.",
  "access.ask.what": "Do que precisa",
  "access.ask.until": "Até",
  "access.ask.why": "Porque precisa",
  "access.ask.whyHint": "O que está a fazer, e porque isto é o mínimo que o permite",
  "access.ask.send": "Enviar o pedido",
  "access.ask.pickOne": "Escolha do que precisa",
  "access.ask.pastDate": "Indique uma data futura, ou deixe em aberto.",
  "access.ask.sent": "Enviado. É o caso {reference}.",
  "cases.kind.access": "Pedido de acesso",
  "cases.kind.customer": "Cliente",
  "cases.kind": "Tipo",
  "cases.access.wants": "Pede",
  "cases.access.until": "Até",
  "cases.access.grant": "Conceder",
  "cases.access.refuse": "Recusar",
  "cases.access.own": "Não pode responder ao seu próprio pedido.",
  "cases.access.explain":
    "Concedido apenas a esta pessoa, nunca à sua equipa. Termina na data pedida, e qualquer pessoa com autoridade pode cortá-lo antes.",
  "cases.priority": "Prioridade: {priority}",
  "cases.priority.low": "Baixa",
  "cases.priority.normal": "Normal",
  "cases.priority.high": "Alta",
  "cases.priority.urgent": "Urgente",
  "cases.unknownCaller": "Autor da chamada desconhecido",
  "cases.unknownCallerPhone": "Autor da chamada desconhecido · {phone}",
  "cases.customerWithPhone": "{name} · {phone}",
  "cases.ownedBy": "atribuído a {name}",
  "cases.unassigned": "não atribuído",
  "cases.closedBecause": "Fechado · motivo: {reason}",
  "cases.timeline.aria": "Cronologia do processo",
  "cases.timeline.empty": "Ainda nada foi registado neste processo.",
  "cases.internal": "Interno",
  "cases.entry.call": "Chamada",
  "cases.entry.callWithDuration": "Chamada ({duration})",
  "cases.entry.note": "Nota interna",
  "cases.entry.chatIn": "Mensagem do cliente na app",
  "cases.entry.chatOut": "Resposta na app",
  "cases.entry.smsIn": "SMS do cliente",
  "cases.entry.smsOut": "Resposta por SMS",
  "cases.entry.smsOutTo": "SMS para {phone}",
  "cases.entry.emailIn": "E-mail do cliente",
  "cases.entry.emailOut": "Resposta por e-mail",
  "cases.sms.pending": "A enviar",
  "cases.sms.sent": "Enviado",
  "cases.sms.failed": "Não enviado",
  "cases.action.claim": "Assumir",
  "cases.action.reassign": "Reatribuir",
  "cases.action.link": "Associar a um cliente",
  "cases.action.relink": "Mudar de cliente",
  "cases.action.close": "Fechar",
  "cases.needs": "Requer {feature}",
  "cases.sending": "A enviar…",
  "cases.cancel": "Cancelar",
  "cases.reload": "Recarregar o processo",
  "cases.reason": "Motivo",
  "cases.reasonHint": "Registado no registo de auditoria com o seu nome.",
  "cases.stale":
    "Este processo mudou enquanto o consultava. Recarregue-o, veja o que mudou e tente novamente: o seu texto é mantido.",
  "cases.claim.done": "{reference} é agora seu.",
  "cases.claim.taken": "Tarde demais: {name} acabou de assumir este processo. Escolha outro.",
  "cases.composer.aria": "Escrever no processo",
  "cases.composer.reply": "Responder",
  "cases.composer.note": "Nota interna",
  "cases.composer.call": "Registar uma chamada",
  "cases.reply.noChannel":
    "Nenhuma conta de cliente associada: associe o cliente para responder na aplicação, ou ligue-lhe de volta.",
  "cases.reply.notified": "O cliente recebe uma notificação na aplicação. Nunca um SMS.",
  "cases.reply.label": "Resposta a {name}",
  "cases.reply.sendChat": "Enviar na app",
  "cases.reply.doneChat": "Resposta enviada na app.",
  "cases.reply.closed": "Este processo está fechado: já não recebe respostas.",
  "cases.reply.unassigned": "Assuma este processo para responder ao cliente.",
  "cases.reply.notOwner": "Só o responsável pelo processo responde ao cliente. Peça uma reatribuição se necessário.",
  "cases.refused.claimed": "Tarde demais: outra pessoa acabou de assumir este processo. Escolha outro.",
  "cases.refused.closed": "Este processo foi entretanto encerrado. Recarregue-o.",
  "cases.refused.notOwner": "Este processo pertence agora a outra pessoa. Recarregue-o, ou peça que seja reatribuído.",
  "cases.refused.noCustomer":
    "Nenhuma conta de cliente associada: associe o cliente para responder na aplicação, ou ligue-lhe de volta.",
  "cases.note.hint": "Visível apenas para a equipa, nunca para o cliente.",
  "cases.note.label": "Nota interna",
  "cases.note.submit": "Adicionar a nota",
  "cases.note.done": "Nota interna adicionada.",
  "cases.call.label": "O que foi dito (resumo interno)",
  "cases.call.minutes": "Duração (minutos)",
  "cases.call.submit": "Registar a chamada",
  "cases.call.done": "Chamada registada.",
  "cases.reassign.title": "Reatribuir {reference}",
  "cases.reassign.from": "Atualmente atribuído a {name}.",
  "cases.reassign.unassigned": "Este processo ainda não está atribuído a ninguém.",
  "cases.reassign.to": "Novo responsável",
  "cases.reassign.choose": "Escolher um membro da equipa",
  "cases.reassign.loading": "A carregar a equipa…",
  "cases.reassign.toId": "Identificador do membro da equipa",
  "cases.reassign.idHint": "Sem acesso à lista da equipa, cole o identificador do membro.",
  "cases.reassign.submit": "Reatribuir",
  "cases.reassign.done": "Processo reatribuído a {name}.",
  "cases.close.title": "Fechar {reference}",
  "cases.close.body": "O cliente já não poderá responder; se voltar, será aberto um novo processo.",
  "cases.close.submit": "Fechar o processo",
  "cases.close.done": "{reference} está fechado.",
  "cases.link.title": "Associar {reference} a um cliente",
  "cases.link.body": "Encontre a conta do cliente que contactou o apoio.",
  "cases.link.caller": "O autor da chamada indicou {phone}. Encontre a sua conta.",
  "cases.link.current": "Atualmente associado a {name}.",
  "cases.link.submit": "Associar",
  "cases.link.done": "Processo associado a {name}.",
  "cases.customer.search": "Procurar o cliente",
  "cases.customer.placeholder": "Nome, telefone, e-mail ou identificador",
  "cases.customer.searchButton": "Procurar",
  "cases.customer.searching": "A procurar…",
  "cases.customer.none": "Nenhuma conta encontrada.",
  "cases.customer.results": "Contas encontradas",
  "cases.customer.id": "Identificador da conta do cliente",
  "cases.customer.idHint": "A pesquisa requer {feature}: cole o identificador da conta.",
  "cases.newCall.title": "Registar uma chamada",
  "cases.newCall.who": "Quem ligou?",
  "cases.newCall.known": "Um cliente",
  "cases.newCall.unknown": "Um autor de chamada desconhecido",
  "cases.newCall.phone": "Número de quem ligou",
  "cases.newCall.phoneHint": "No formato internacional, por exemplo +224 621 23 45 67.",
  "cases.newCall.subject": "Assunto",
  "cases.newCall.internal": "Interno: o cliente nunca vê este resumo.",
  "cases.newCall.priority": "Prioridade",
  "cases.newCall.submit": "Abrir o processo",
  "cases.problem.reason": "Diga porquê, em poucas palavras.",
  "cases.problem.body": "Escreva alguma coisa.",
  "cases.problem.tooLong": "Demasiado longo: no máximo {max} caracteres.",
  "cases.problem.minutes": "A duração é um número inteiro de minutos, de 0 a {max}.",
  "cases.problem.phone": "O número deve estar no formato internacional, por exemplo +224621234567.",
  "cases.problem.pickCustomer": "Escolha o cliente.",
  "cases.problem.customerId": "Este identificador de conta não tem o formato correto.",
  "cases.problem.pickStaff": "Escolha quem fica com o processo.",
  "cases.problem.staffId": "Este identificador de membro não tem o formato correto.",
  "cases.problem.subject": "Dê ao processo um assunto curto.",
  // ── Verificações ──
  "verifications.summary.open": "{count} em espera · a mais antiga há {age}",
  "verifications.summary.none": "Nenhuma verificação em espera",
  "verifications.summary.shown": "{count} verificações apresentadas",
  "verifications.filter.status": "Estado",
  "verifications.filter.status.open": "Todas em espera",
  "verifications.filter.status.submitted": "A analisar",
  "verifications.filter.status.flagged": "Documento banido",
  "verifications.filter.status.approved": "Aprovadas",
  "verifications.filter.status.rejected": "Recusadas",
  "verifications.filter.document": "Tipo de documento",
  "verifications.filter.document.all": "Todos",
  "verifications.filter.document.national_id": "Cartão de identidade",
  "verifications.filter.document.passport": "Passaporte",
  "verifications.queue.aria": "Fila de verificações, a mais antiga primeiro",
  "verifications.queue.loading": "A carregar a fila…",
  "verifications.queue.loadFailed": "Não foi possível carregar a fila.",
  "verifications.queue.emptyOpen": "Nenhuma verificação em espera: a fila está vazia.",
  "verifications.queue.empty": "Nenhuma verificação corresponde a estes filtros.",
  "verifications.queue.waited": "Tempo de espera desde o envio",
  "verifications.queue.end": "Fim da fila.",
  "verifications.flag.banned": "Documento banido",
  "verifications.status.submitted": "A analisar",
  "verifications.status.flagged": "Documento banido",
  "verifications.status.approved": "Aprovada",
  "verifications.status.rejected": "Recusada",
  "verifications.document.national_id": "Cartão de identidade",
  "verifications.document.passport": "Passaporte",
  "verifications.side.front": "Frente",
  "verifications.side.back": "Verso do cartão",
  "verifications.side.selfie": "Fotografia selfie",
  "verifications.detail.pick": "Escolha uma verificação na fila para a analisar.",
  "verifications.detail.loading": "A carregar a verificação…",
  "verifications.detail.loadFailed": "Não foi possível carregar esta verificação.",
  "verifications.detail.notFound": "Nenhuma verificação tem este identificador. Recarregue a fila.",
  "verifications.detail.noPhone": "sem telefone",
  "verifications.detail.submitted": "enviada a {date}",
  "verifications.detail.versionTitle": "Versão analisada: uma decisão só se aplica a esta versão",
  "verifications.banned.title": "Documento banido",
  "verifications.banned.body":
    "Este número corresponde a um documento banido a {date} · motivo: {reason}. Nunca pode ser aprovado: recuse a verificação.",
  "verifications.banned.bodyNoReason":
    "Este número corresponde a um documento banido. Nunca pode ser aprovado: recuse a verificação.",
  "verifications.banned.notTold": "O pro não é informado de que o seu documento está banido.",
  "verifications.images.aria": "Documentos de identidade",
  "verifications.images.show": "Mostrar os documentos",
  "verifications.images.showAgain": "Mostrar novamente",
  "verifications.images.loading": "A carregar…",
  "verifications.images.notice":
    "Cada visualização fica registada no registo de auditoria, em seu nome, e aparece no histórico do pro. As ligações expiram ao fim de 2 minutos.",
  "verifications.images.recorded": "Visualização registada no registo de auditoria · ligações válidas até às {time}",
  "verifications.images.hide": "Ocultar",
  "verifications.images.expired":
    "As ligações expiraram e as imagens foram retiradas do ecrã. Mostrá-las novamente também fica registado.",
  "verifications.images.locked": "As imagens de identidade requerem",
  "verifications.images.deleted": "As imagens foram apagadas a {date}, como exige a regra de conservação.",
  "verifications.col.field": "Campo",
  "verifications.col.read": "Lido no documento",
  "verifications.col.typed": "Introduzido pelo pro",
  "verifications.col.match": "Correspondência",
  "verifications.field.name": "Nome",
  "verifications.field.dateOfBirth": "Nascido a",
  "verifications.field.expiresOn": "Expira a",
  "verifications.field.documentNumber": "N.º do documento",
  "verifications.notAsked": "não pedido",
  "verifications.match.check": "Verificar na imagem",
  "verifications.match.same": "Corresponde",
  "verifications.match.different": "Não corresponde",
  "verifications.match.readOnly": "Apenas lido",
  "verifications.match.expired": "Expirado",
  "verifications.match.valid": "Válido",
  "verifications.match.banned": "Banido",
  "verifications.match.notBanned": "Não banido",
  "verifications.last4Hint": "Só são guardados os últimos 4 caracteres do número ({last4}): compare-os com a imagem.",
  "verifications.standing.title": "Conta do pro hoje",
  "verifications.standing.verificationStatus": "Estado da verificação",
  "verifications.standing.approvalStatus": "Aprovação do perfil",
  "verifications.standing.isVerified": "Distintivo «verificado»",
  "verifications.standing.status.unverified": "Não verificado",
  "verifications.standing.status.pending": "Em análise",
  "verifications.standing.status.verified": "Verificado",
  "verifications.standing.status.rejected": "Recusado",
  "verifications.standing.approval.pending": "Por aprovar",
  "verifications.standing.approval.approved": "Aprovado",
  "verifications.standing.approval.rejected": "Não aprovado",
  "verifications.yes": "Sim",
  "verifications.no": "Não",
  "verifications.previous.title": "Envios anteriores",
  "verifications.previous.none": "Primeiro envio deste pro.",
  "verifications.approve.button": "Aprovar o pro",
  "verifications.reject.button": "Recusar",
  "verifications.decided": "Esta verificação já foi decidida.",
  "verifications.approve.banned":
    "Não é possível aprovar: o número corresponde a um documento banido. Recuse a verificação.",
  "verifications.approve.locked":
    "Aprovar também requer APPROVE_PROS_STAFF_FEATURE_ENABLEMENT, que as suas funções não incluem.",
  "verifications.decided.approved": "Aprovada por {name} a {date}.",
  "verifications.decided.rejected": "Recusada por {name} a {date}.",
  "verifications.decided.reasonCode": "Motivo: {reason}",
  "verifications.decided.reason": "Nota da equipa: «{reason}»",
  "verifications.outcome.approved":
    "{name} está aprovado: o perfil está verificado e pode ser encontrado e contratado.",
  "verifications.outcome.rejected": "Verificação recusada. O profissional é avisado na aplicação, na sua língua.",
  "verifications.approve.title": "Aprovar {name}",
  "verifications.approve.body":
    "O documento é aceite e o pro fica verificado: pode ser encontrado e contratado. A decisão fica registada no registo de auditoria com o seu motivo.",
  "verifications.approve.reason": "Motivo (registado no registo de auditoria)",
  "verifications.approve.hint": "Por ex. «Cartão legível, selfie confere».",
  "verifications.approve.submit": "Aprovar o pro",
  "verifications.approve.tooShort": "Indique um motivo com pelo menos {min} caracteres.",
  "verifications.reject.title": "Recusar a verificação de {name}",
  "verifications.reject.body": "O pro poderá enviar um novo documento a partir da aplicação.",
  "verifications.reject.preset": "Motivo da recusa",
  "verifications.reject.presetNone": "Escolher um motivo…",
  "verifications.reject.code.photo_unreadable": "Foto ilegível",
  "verifications.reject.code.selfie_mismatch": "A selfie não corresponde",
  "verifications.reject.code.document_expired": "Documento expirado",
  "verifications.reject.code.document_not_accepted": "Documento não aceite",
  "verifications.reject.code.other": "Outro",
  "verifications.reject.reason": "Nota para a equipa (opcional)",
  "verifications.reject.noteHint":
    "Nunca mostrada ao profissional: ele recebe na aplicação a mensagem do motivo escolhido, na sua língua.",
  "verifications.reject.submit": "Recusar a verificação",
  "verifications.reject.pickCode": "Escolha o motivo da recusa.",
  "verifications.reason.tooLong": "O motivo pode ter no máximo {max} caracteres.",
  "verifications.reason.count": "{count} de {max} caracteres",
  "verifications.dialog.version": "Decisão sobre a versão {version}",
  "verifications.dialog.cancel": "Cancelar",
  "verifications.dialog.sending": "A enviar…",
  "verifications.error.stale":
    "Esta verificação mudou enquanto a estava a ver (talvez outra pessoa a tenha decidido). Recarregue para ver a versão atual e decida novamente.",
  "verifications.error.approveRefused":
    "Aprovação recusada: as suas funções não incluem REVIEW_VERIFICATIONS e APPROVE_PROS, ou o documento acabou de ser banido. Recarregue.",
  "verifications.error.rejectRefused":
    "Não é possível recusar: as suas funções já não incluem REVIEW_VERIFICATIONS. Peça acesso a um responsável.",
  "verifications.error.imagesRefused":
    "Imagens recusadas: as suas funções já não incluem VIEW_ID_IMAGES. Esta tentativa fica registada no registo de auditoria.",
  "verifications.error.approveInvalid":
    "Aprovação recusada: a verificação já foi decidida, o documento está banido ou o motivo é inválido. Recarregue e verifique.",
  "verifications.error.rejectInvalid":
    "Recusa rejeitada: a verificação já foi decidida, ou a nota tem mais de {max} caracteres.",
  "verifications.error.imagesDeleted":
    "As imagens já não estão disponíveis: foram apagadas pela regra de conservação, ou a verificação não foi encontrada.",
  "verifications.error.outcomeUnknown":
    "Sem resposta do servidor: a decisão pode ter sido guardada. Tente novamente: o mesmo clique não pode ser aplicado duas vezes.",
};
